/**
 * Packet encoding.
 *
 * Every packet is a one byte opcode followed by fields in a fixed order. Encode and
 * decode for each one sit next to each other deliberately: they have to change
 * together, and splitting them across files is how a protocol drifts out of sync.
 *
 * Positions travel as varints quantized to 1/64 of a tile, relative to the region
 * origin. Two bytes each near the origin and three across a continent, against
 * eight for a pair of floats, and no ceiling: a fixed sixteen bit field would
 * have covered a thousand tiles and quietly wrapped a position round the far
 * side of anything larger, which is exactly what it did.
 */

import { POSITION_PRECISION, type WorldPos } from '@inverse/common';
import { ByteReader, ByteWriter } from './buffer';
import { ClientOp, ServerOp } from './opcodes';

function writePosition(writer: ByteWriter, pos: WorldPos, originX: number, originZ: number): void {
  writer.writeVarInt(Math.max(0, Math.round((pos.x - originX) * POSITION_PRECISION)));
  writer.writeVarInt(Math.max(0, Math.round((pos.z - originZ) * POSITION_PRECISION)));
  writer.writeU8(pos.plane);
}

function readPosition(reader: ByteReader, originX: number, originZ: number): WorldPos {
  const x = reader.readVarInt() / POSITION_PRECISION + originX;
  const z = reader.readVarInt() / POSITION_PRECISION + originZ;
  return { x, z, plane: reader.readU8() };
}

/** Heading is only ever used to face a model, so a byte of precision is plenty. */
function writeHeading(writer: ByteWriter, heading: number): void {
  writer.writeI8(Math.round((heading / Math.PI) * 127));
}

function readHeading(reader: ByteReader): number {
  return (reader.readI8() / 127) * Math.PI;
}

// ---------------------------------------------------------------------------
// Client to server
// ---------------------------------------------------------------------------

export interface HandshakePacket {
  version: number;
  token: string;
}

export function encodeHandshake(packet: HandshakePacket): Uint8Array {
  const writer = new ByteWriter(64);
  writer.writeU8(ClientOp.Handshake).writeU16(packet.version).writeString(packet.token);
  return writer.toUint8Array();
}

export function decodeHandshake(reader: ByteReader): HandshakePacket {
  return { version: reader.readU16(), token: reader.readString(512) };
}

export interface InputFrameWire {
  sequence: number;
  running: boolean;
}

/**
 * Input frames are sent in small batches rather than one per frame. A dropped packet
 * then costs nothing, because the next one still carries the frames it covered.
 *
 * A frame carries no direction. Where a player is going is a route, sent once when
 * they ask to go there; what these carry is the passage of time, whether they are
 * running, and a sequence the server can acknowledge.
 */
export function encodeInputFrames(frames: readonly InputFrameWire[]): Uint8Array {
  const writer = new ByteWriter(16 + frames.length * 4);
  writer.writeU8(ClientOp.InputFrames).writeU8(frames.length);
  for (const frame of frames) {
    writer.writeVarInt(frame.sequence).writeBool(frame.running);
  }
  return writer.toUint8Array();
}

export function decodeInputFrames(reader: ByteReader, maxFrames = 32): InputFrameWire[] {
  const count = reader.readU8();
  if (count > maxFrames) throw new RangeError(`input batch of ${count} exceeds ${maxFrames}`);

  const frames: InputFrameWire[] = [];
  for (let i = 0; i < count; i++) {
    frames.push({ sequence: reader.readVarInt(), running: reader.readBool() });
  }
  return frames;
}

/**
 * Largest route a client may propose.
 *
 * A route is one point per tile now rather than one per corner, so this is a
 * length in tiles: longer than the width of the area the search will explore, so
 * any route the search can produce fits.
 */
export const MAX_ROUTE_POINTS = 200;

export interface MoveToPacket {
  tx: number;
  tz: number;
  plane: number;
  /**
   * The route the client is already walking, as tiles.
   *
   * Sent so that both sides walk the same line rather than each running its own
   * search from its own position: the two positions differ by the trip time, and
   * around a corner that difference is enough to produce two different routes,
   * which the player sees as being dragged sideways.
   *
   * It is a proposal, not an instruction. The server checks every leg against its
   * own collision map and falls back to its own search if any of it is wrong.
   */
  route: { tx: number; tz: number }[];
}

export function encodeMoveTo(packet: MoveToPacket): Uint8Array {
  const points = packet.route.slice(0, MAX_ROUTE_POINTS);
  const writer = new ByteWriter(16 + points.length * 6);
  writer
    .writeU8(ClientOp.MoveTo)
    .writeVarInt(packet.tx)
    .writeVarInt(packet.tz)
    .writeU8(packet.plane)
    .writeU8(points.length);

  for (const point of points) writer.writeVarInt(point.tx).writeVarInt(point.tz);
  return writer.toUint8Array();
}

export function decodeMoveTo(reader: ByteReader): MoveToPacket {
  const tx = reader.readVarInt();
  const tz = reader.readVarInt();
  const plane = reader.readU8();

  const count = reader.readU8();
  if (count > MAX_ROUTE_POINTS) throw new RangeError(`route of ${count} points is too long`);

  const route = new Array<{ tx: number; tz: number }>(count);
  for (let i = 0; i < count; i++) route[i] = { tx: reader.readVarInt(), tz: reader.readVarInt() };

  return { tx, tz, plane, route };
}

export function encodeSetRunning(running: boolean): Uint8Array {
  return new ByteWriter(2).writeU8(ClientOp.SetRunning).writeBool(running).toUint8Array();
}

export function encodeChat(message: string): Uint8Array {
  return new ByteWriter(128).writeU8(ClientOp.Chat).writeString(message).toUint8Array();
}

export function encodePing(clientTime: number): Uint8Array {
  return new ByteWriter(16).writeU8(ClientOp.Ping).writeF64(clientTime).toUint8Array();
}

/** What a menu entry was clicked on. */
export const TargetKind = {
  Entity: 1,
  Object: 2,
  /** A pile on the ground, identified by the pile rather than the item in it. */
  GroundItem: 3,
  /** Another slot in the bag, for one item used on another. */
  Item: 4,
} as const;

export type TargetKindValue = (typeof TargetKind)[keyof typeof TargetKind];

export interface InteractPacket {
  kind: TargetKindValue;
  /** Entity id, or object definition id. */
  id: number;
  /** Tile the object stands on. Zero for entities, which carry their own position. */
  tx: number;
  tz: number;
  plane: number;
  /** Index into that target's action list, as the client was told it at login. */
  action: number;
}

export function encodeInteract(packet: InteractPacket): Uint8Array {
  const writer = new ByteWriter(24);
  writer
    .writeU8(ClientOp.Interact)
    .writeU8(packet.kind)
    .writeVarInt(packet.id)
    .writeVarInt(packet.tx)
    .writeVarInt(packet.tz)
    .writeU8(packet.plane)
    .writeU8(packet.action);
  return writer.toUint8Array();
}

export function decodeInteract(reader: ByteReader): InteractPacket {
  return {
    kind: reader.readU8() as TargetKindValue,
    id: reader.readVarInt(),
    tx: reader.readVarInt(),
    tz: reader.readVarInt(),
    plane: reader.readU8(),
    action: reader.readU8(),
  };
}

export interface PrivateMessageRequest {
  to: string;
  message: string;
}

export function encodePrivateMessageRequest(packet: PrivateMessageRequest): Uint8Array {
  return new ByteWriter(192)
    .writeU8(ClientOp.PrivateMessage)
    .writeString(packet.to)
    .writeString(packet.message)
    .toUint8Array();
}

export function decodePrivateMessageRequest(reader: ByteReader): PrivateMessageRequest {
  return { to: reader.readString(32), message: reader.readString(256) };
}

/**
 * A change to who somebody has written down.
 *
 * Which list and which direction are one number rather than a pair of flags:
 * the four are exclusive and naming them out loud reads better at both ends
 * than a boolean called `add`.
 */
export const FriendAct = {
  /**
   * Asks somebody to be a friend, rather than writing them down.
   *
   * Being on a list used to be something done to somebody: adding a name let the
   * adder see whether they were online and where they were standing, and the
   * person named was never told and never asked. Now it puts a request in front
   * of them, and nothing is shared until they say yes.
   */
  AskFriend: 0,
  RemoveFriend: 1,
  Ignore: 2,
  StopIgnoring: 3,
  /** Taking back a request that has not been answered. */
  UnaskFriend: 4,
  /** Answering one that has arrived. */
  AcceptFriend: 5,
  DenyFriend: 6,
} as const;

export type FriendActValue = (typeof FriendAct)[keyof typeof FriendAct];

export interface FriendsRequest {
  act: FriendActValue;
  name: string;
}

export function encodeFriendsRequest(packet: FriendsRequest): Uint8Array {
  return new ByteWriter(64)
    .writeU8(ClientOp.Friends)
    .writeU8(packet.act)
    .writeString(packet.name)
    .toUint8Array();
}

export function decodeFriendsRequest(reader: ByteReader): FriendsRequest {
  return { act: reader.readU8() as FriendActValue, name: reader.readString(32) };
}

/**
 * Where the editing camera is looking.
 *
 * A tile, and whether the camera has left the character at all. Sent when it
 * has moved far enough to matter rather than every frame: what it decides is
 * which squares of ground to send, and that is measured in sixty four tile
 * squares.
 */
export interface WatchingPacket {
  /** Whether the camera is off on its own. False puts the world back on the character. */
  away: boolean;
  tx: number;
  tz: number;
}

export function encodeWatching(packet: WatchingPacket): Uint8Array {
  return new ByteWriter(16)
    .writeU8(ClientOp.Watching)
    .writeBool(packet.away)
    .writeVarInt(packet.tx)
    .writeVarInt(packet.tz)
    .toUint8Array();
}

export function decodeWatching(reader: ByteReader): WatchingPacket {
  return { away: reader.readBool(), tx: reader.readVarInt(), tz: reader.readVarInt() };
}

export interface MoveItemPacket {
  container: number;
  from: number;
  to: number;
}

export function encodeMoveItem(packet: MoveItemPacket): Uint8Array {
  return new ByteWriter(8)
    .writeU8(ClientOp.MoveItem)
    .writeU8(packet.container)
    .writeVarInt(packet.from)
    .writeVarInt(packet.to)
    .toUint8Array();
}

export function decodeMoveItem(reader: ByteReader): MoveItemPacket {
  return { container: reader.readU8(), from: reader.readVarInt(), to: reader.readVarInt() };
}

export interface ItemActionPacket {
  container: number;
  slot: number;
  /** Index into the item's menu entries. The server decides what that means. */
  action: number;
}

export function encodeItemAction(packet: ItemActionPacket): Uint8Array {
  return new ByteWriter(8)
    .writeU8(ClientOp.ItemAction)
    .writeU8(packet.container)
    .writeVarInt(packet.slot)
    .writeU8(packet.action)
    .toUint8Array();
}

export function decodeItemAction(reader: ByteReader): ItemActionPacket {
  return { container: reader.readU8(), slot: reader.readVarInt(), action: reader.readU8() };
}

export interface DropItemPacket {
  container: number;
  slot: number;
  /** How many to drop. Everything in the slot when this is zero. */
  quantity: number;
}

export function encodeDropItem(packet: DropItemPacket): Uint8Array {
  return new ByteWriter(12)
    .writeU8(ClientOp.DropItem)
    .writeU8(packet.container)
    .writeVarInt(packet.slot)
    .writeVarInt(packet.quantity)
    .toUint8Array();
}

export function decodeDropItem(reader: ByteReader): DropItemPacket {
  return { container: reader.readU8(), slot: reader.readVarInt(), quantity: reader.readVarInt() };
}

// ---------------------------------------------------------------------------
// Server to client
// ---------------------------------------------------------------------------

export interface WelcomePacket {
  entityId: number;
  shardId: number;
  shardName: string;
  serverTime: number;
}

export function encodeWelcome(packet: WelcomePacket): Uint8Array {
  const writer = new ByteWriter(64);
  writer
    .writeU8(ServerOp.Welcome)
    .writeU32(packet.entityId)
    .writeU8(packet.shardId)
    .writeString(packet.shardName)
    .writeF64(packet.serverTime);
  return writer.toUint8Array();
}

export function decodeWelcome(reader: ByteReader): WelcomePacket {
  return {
    entityId: reader.readU32(),
    shardId: reader.readU8(),
    shardName: reader.readString(64),
    serverTime: reader.readF64(),
  };
}

export function encodeReject(reason: number): Uint8Array {
  return new ByteWriter(2).writeU8(ServerOp.Reject).writeU8(reason).toUint8Array();
}

export function encodePong(clientTime: number, serverTime: number): Uint8Array {
  return new ByteWriter(24)
    .writeU8(ServerOp.Pong)
    .writeF64(clientTime)
    .writeF64(serverTime)
    .toUint8Array();
}

/**
 * One thing standing on the ground.
 *
 * Arrives with the square of ground it stands on rather than with the region,
 * because a continent has hundreds of thousands of them and only the ones near
 * somebody are any use to them.
 */
export interface PlacedObject {
  id: number;
  tx: number;
  tz: number;
  plane: number;
  rotation: number;
  /** Drawing size, where 1 is the size it was modelled at. */
  scale?: number;
  /** A height of its own, for anything built out over water. */
  lift?: number;
  /**
   * A shape of its own, borrowed from something else.
   *
   * One kind of tree stands in a wood of pines and oaks: what it is for
   * belongs to the kind, and what it looks like belongs to the one standing
   * here. Nought is drawn however its kind is drawn.
   */
  looks?: number;
  /**
   * What this one can be worked for, when it differs from its kind.
   *
   * Only the names of the ways: what comes up when one is worked is the
   * world's business, and a client that was told would be a client that
   * could be asked.
   */
  ways?: { action: string; catches: { itemId: number; weight: number }[] }[];
}

/**
 * Runs of the same byte, which is most of a chart.
 *
 * An ocean is thousands of readings all saying the same thing and a mountain
 * range is hundreds. Written as a count and a value, the whole of a continent
 * comes down from fifty kilobytes to a few, and a picture of a place nobody has
 * walked yet stops costing more than the ground under their feet.
 */
function writeRuns(writer: ByteWriter, held: ArrayLike<number>): void {
  let at = 0;
  const runs: [number, number][] = [];

  while (at < held.length) {
    const value = held[at] ?? 0;
    let run = 1;
    while (at + run < held.length && held[at + run] === value) run++;
    runs.push([run, value]);
    at += run;
  }

  writer.writeVarInt(runs.length);
  for (const [run, value] of runs) writer.writeVarInt(run).writeU8(value);
}

function readRuns(reader: ByteReader, count: number): Uint8Array {
  const held = new Uint8Array(count);
  const runs = reader.readVarInt();
  let at = 0;

  for (let index = 0; index < runs; index++) {
    const run = reader.readVarInt();
    const value = reader.readU8();
    for (let step = 0; step < run && at < count; step++) held[at++] = value;
  }

  return held;
}

/** A run of the same byte, which most of a square usually is. */
const PLAIN_UNIFORM = 0;
const PLAIN_EXPLICIT = 1;

function writeBytes(writer: ByteWriter, held: ArrayLike<number>): void {
  const first = held[0] ?? 0;
  let same = true;
  for (let at = 1; at < held.length && same; at++) same = held[at] === first;

  if (same) {
    writer.writeU8(PLAIN_UNIFORM).writeU8(first);
    return;
  }

  writer.writeU8(PLAIN_EXPLICIT);
  for (let at = 0; at < held.length; at++) writer.writeU8(held[at] ?? 0);
}

function readBytes(reader: ByteReader, count: number): Uint8Array {
  if (reader.readU8() === PLAIN_UNIFORM) return new Uint8Array(count).fill(reader.readU8());

  const held = new Uint8Array(count);
  for (let at = 0; at < count; at++) held[at] = reader.readU8();
  return held;
}

export interface RegionPacket {
  id: string;
  name: string;
  originX: number;
  originZ: number;
  width: number;
  height: number;
  /** Somewhere with rock overhead: no sea, no sun. */
  underground: boolean;
  /**
   * Ground that is called something: a town, a district, a wood.
   *
   * Sent to everybody rather than only to the tools, because the name of where
   * you are standing belongs on the map in the corner. Only the places that
   * name ground are here; a place that merely does something when walked into
   * is the world's business.
   */
  places: {
    id: number;
    name: string;
    /** A line about the place, shown when somebody points at it on the map. */
    about: string;
    tx: number;
    tz: number;
    width: number;
    height: number;
    plane: number;
  }[];
  /**
   * The whole place at a glance, one reading every few tiles.
   *
   * The ground arrives a square at a time, so a map drawn only from what has
   * arrived shows a hole where a continent should be. This is the rest of it,
   * coarse enough to be cheap and detailed enough to navigate by: what each
   * block is made of and how high it stands. The squares that have arrived are
   * drawn over the top at full detail.
   */
  chart: Chart;
}

/**
 * Where somebody now is: which place, how big it is, and what parts of it are
 * called something.
 *
 * The ground itself does not travel with this any more. A region used to arrive
 * whole, which is a hundred kilobytes for an island and seventy six megabytes
 * for a continent; it arrives a square at a time instead, and this packet is
 * what tells the client how large a place to make room for.
 */
export function encodeRegion(
  region: Omit<RegionPacket, 'chart'> & { chart: ChartWire },
): Uint8Array {
  const writer = new ByteWriter(region.places.length * 80 + 512);
  writer
    .writeU8(ServerOp.RegionData)
    .writeString(region.id)
    .writeString(region.name)
    .writeVarInt(region.originX)
    .writeVarInt(region.originZ)
    .writeU16(region.width)
    .writeU16(region.height)
    .writeBool(region.underground);

  // Ground that is called something. Few and small, and sent with the region
  // rather than with the ground: the name of where you are belongs on the map
  // in the corner from the moment you arrive, and a name costs nothing.
  writer.writeVarInt(region.places.length);
  for (const place of region.places) {
    writer
      .writeVarInt(place.id)
      .writeString(place.name)
      .writeString(place.about)
      .writeVarInt(place.tx)
      .writeVarInt(place.tz)
      .writeU16(place.width)
      .writeU16(place.height)
      .writeU8(place.plane);
  }

  writer.writeU8(region.chart.step).writeU16(region.chart.width).writeU16(region.chart.height);
  writeRuns(writer, region.chart.kinds);
  writeRuns(writer, region.chart.heights);
  writeChartAreas(writer, region.chart);

  return writer.toUint8Array();
}

/** Everything standing on a stretch of ground, however that stretch was chosen. */
function writeObjects(writer: ByteWriter, objects: PlacedObject[]): void {
  writer.writeVarInt(objects.length);

  for (const object of objects) {
    writer
      .writeVarInt(object.id)
      .writeU16(object.tx)
      .writeU16(object.tz)
      .writeU8(object.plane)
      .writeU8(object.rotation)
      // Sent with the ground as well as with a patch, or everything resized in
      // the editor goes back to its modelled size the next time somebody logs in.
      .writeU8(Math.round(Math.min(Math.max(object.scale ?? 1, 0.25), 4) * 16))
      // A dock holds its own height. One byte says whether this piece has one,
      // which is cheaper across a whole region than sending a height for the
      // thousands of things that simply stand on the ground.
      .writeU8(object.lift === undefined ? 0 : 1);

    if (object.lift !== undefined) writer.writeI16(Math.round(object.lift * 32));

    // A shape of its own, or nought to be drawn however its kind is drawn.
    writer.writeVarInt(object.looks ?? 0);

    // What this one can be worked for, when it differs from its kind. Sent so
    // the menu on a particular fishing spot offers what that spot actually
    // holds rather than everything its kind knows how to do.
    const ways = object.ways?.slice(0, 8);
    writer.writeU8(ways === undefined ? 0 : Math.max(1, ways.length + 1));

    for (const way of ways ?? []) writer.writeString(way.action);
  }
}

/** Everything standing on a stretch of ground, read back. */
function readObjects(reader: ByteReader): PlacedObject[] {
  const count = reader.readVarInt();
  const objects = new Array<PlacedObject[][number]>(count);

  for (let i = 0; i < count; i++) {
    const object = {
      id: reader.readVarInt(),
      tx: reader.readU16(),
      tz: reader.readU16(),
      plane: reader.readU8(),
      rotation: reader.readU8(),
      scale: reader.readU8() / 16,
    };

    const lifted = reader.readU8() === 1 ? { ...object, lift: reader.readI16() / 32 } : object;

    const looks = reader.readVarInt();
    const held = looks > 0 ? { ...lifted, looks } : lifted;

    // Nought means this one is worked however its kind is worked. Anything else
    // is a count of the ways it offers, one past itself so that a spot offering
    // none of them can be told from a spot that never said.
    const marked = reader.readU8();
    objects[i] =
      marked === 0
        ? held
        : {
            ...held,
            ways: Array.from({ length: marked - 1 }, () => ({
              action: reader.readString(24),
              catches: [],
            })),
          };
  }

  return objects;
}

export function decodeRegion(reader: ByteReader): RegionPacket {
  const id = reader.readString(64);
  const name = reader.readString(64);
  const originX = reader.readVarInt();
  const originZ = reader.readVarInt();
  const width = reader.readU16();
  const height = reader.readU16();
  const underground = reader.readBool();

  const placeCount = reader.readVarInt();
  const places = new Array<RegionPacket['places'][number]>(placeCount);
  for (let i = 0; i < placeCount; i++) {
    places[i] = {
      id: reader.readVarInt(),
      name: reader.readString(48),
      about: reader.readString(160),
      tx: reader.readVarInt(),
      tz: reader.readVarInt(),
      width: reader.readU16(),
      height: reader.readU16(),
      plane: reader.readU8(),
    };
  }

  const step = reader.readU8();
  const chartWidth = reader.readU16();
  const chartHeight = reader.readU16();
  const chart = {
    step,
    width: chartWidth,
    height: chartHeight,
    kinds: readRuns(reader, chartWidth * chartHeight),
    heights: readRuns(reader, chartWidth * chartHeight),
    ...readChartAreas(reader, chartWidth * chartHeight),
  };

  return { id, name, originX, originZ, width, height, underground, places, chart };
}

export const EntityKind = { Player: 0, Npc: 1 } as const;

/**
 * What somebody looks like, beyond what they are wearing.
 *
 * The model they are drawn with and the two colours that are theirs rather than
 * their clothes. NPCs use the same shape: an enemy with several models picks one
 * when it spawns and carries it here, which is how a goblin can be one of a few
 * without being several kinds of goblin.
 */
export interface EntityLook {
  model: string;
  skin: number;
  eyes: number;
}

export const DEFAULT_LOOK: EntityLook = { model: 'person', skin: 0x6b4a35, eyes: 0xf2efe6 };

export interface EntitySpawnPacket {
  entityId: number;
  kind: number;
  /** NPC definition id. Zero for players. */
  defId: number;
  name: string;
  pos: WorldPos;
  heading: number;
  combatLevel: number;
  /** Item id per equipment slot, zero for an empty one. Empty for NPCs. */
  equipment: number[];
  look: EntityLook;
}

function writeLook(writer: ByteWriter, look: EntityLook): void {
  writer.writeString(look.model).writeVarInt(look.skin).writeVarInt(look.eyes);
}

function readLook(reader: ByteReader): EntityLook {
  const model = reader.readString(24);
  const skin = reader.readVarInt();
  const eyes = reader.readVarInt();
  return { model, skin, eyes };
}

/** Worn items, in equipment slot order. Shared by spawns and appearance updates. */
function writeEquipment(writer: ByteWriter, equipment: readonly number[]): void {
  writer.writeU8(equipment.length);
  for (const itemId of equipment) writer.writeVarInt(itemId);
}

function readEquipment(reader: ByteReader): number[] {
  const count = reader.readU8();
  const equipment = new Array<number>(count);
  for (let i = 0; i < count; i++) equipment[i] = reader.readVarInt();
  return equipment;
}

export function encodeEntitySpawn(
  packet: EntitySpawnPacket,
  originX: number,
  originZ: number,
): Uint8Array {
  const writer = new ByteWriter(64);
  writer.writeU8(ServerOp.EntitySpawn).writeU32(packet.entityId).writeU8(packet.kind);
  writer.writeVarInt(packet.defId).writeString(packet.name).writeU16(packet.combatLevel);
  writePosition(writer, packet.pos, originX, originZ);
  writeHeading(writer, packet.heading);
  writeEquipment(writer, packet.equipment);
  writeLook(writer, packet.look);
  return writer.toUint8Array();
}

export function decodeEntitySpawn(
  reader: ByteReader,
  originX: number,
  originZ: number,
): EntitySpawnPacket {
  const entityId = reader.readU32();
  const kind = reader.readU8();
  const defId = reader.readVarInt();
  const name = reader.readString(32);
  const combatLevel = reader.readU16();
  const pos = readPosition(reader, originX, originZ);
  const heading = readHeading(reader);
  const equipment = readEquipment(reader);
  const look = readLook(reader);
  return { entityId, kind, defId, name, combatLevel, pos, heading, equipment, look };
}

export function encodeEntityDespawn(entityIds: readonly number[]): Uint8Array {
  const writer = new ByteWriter(8 + entityIds.length * 4);
  writer.writeU8(ServerOp.EntityDespawn).writeVarInt(entityIds.length);
  for (const id of entityIds) writer.writeU32(id);
  return writer.toUint8Array();
}

export function decodeEntityDespawn(reader: ByteReader): number[] {
  const count = reader.readVarInt();
  const ids = new Array<number>(count);
  for (let i = 0; i < count; i++) ids[i] = reader.readU32();
  return ids;
}

export interface EntityPosition {
  entityId: number;
  x: number;
  z: number;
  plane: number;
  heading: number;
  moving: boolean;
  running: boolean;
}

/**
 * The hot packet. Ten of these a second per player, carrying everything in view,
 * so eleven bytes per entity is a budget rather than an implementation detail.
 */
export function encodeEntityPositions(
  entities: readonly EntityPosition[],
  originX: number,
  originZ: number,
): Uint8Array {
  const writer = new ByteWriter(8 + entities.length * 12);
  writer.writeU8(ServerOp.EntityPositions).writeVarInt(entities.length);

  for (const entity of entities) {
    writer
      .writeU32(entity.entityId)
      .writeVarInt(Math.max(0, Math.round((entity.x - originX) * POSITION_PRECISION)))
      .writeVarInt(Math.max(0, Math.round((entity.z - originZ) * POSITION_PRECISION)))
      .writeU8(entity.plane);
    writeHeading(writer, entity.heading);
    writer.writeU8((entity.moving ? 1 : 0) | (entity.running ? 2 : 0));
  }

  return writer.toUint8Array();
}

export function decodeEntityPositions(
  reader: ByteReader,
  originX: number,
  originZ: number,
): EntityPosition[] {
  const count = reader.readVarInt();
  const entities = new Array<EntityPosition>(count);

  for (let i = 0; i < count; i++) {
    const entityId = reader.readU32();
    const x = reader.readVarInt() / POSITION_PRECISION + originX;
    const z = reader.readVarInt() / POSITION_PRECISION + originZ;
    const plane = reader.readU8();
    const heading = readHeading(reader);
    const state = reader.readU8();
    entities[i] = {
      entityId,
      x,
      z,
      plane,
      heading,
      moving: (state & 1) !== 0,
      running: (state & 2) !== 0,
    };
  }

  return entities;
}

export interface MoveCorrectionPacket {
  /** Last input frame the server applied. Everything after it is replayed by the client. */
  sequence: number;
  pos: WorldPos;
  runEnergy: number;
}

export function encodeMoveCorrection(
  packet: MoveCorrectionPacket,
  originX: number,
  originZ: number,
): Uint8Array {
  const writer = new ByteWriter(24);
  writer.writeU8(ServerOp.MoveCorrection).writeVarInt(packet.sequence);
  writePosition(writer, packet.pos, originX, originZ);
  writer.writeU16(Math.round(packet.runEnergy));
  return writer.toUint8Array();
}

export function decodeMoveCorrection(
  reader: ByteReader,
  originX: number,
  originZ: number,
): MoveCorrectionPacket {
  const sequence = reader.readVarInt();
  const pos = readPosition(reader, originX, originZ);
  return { sequence, pos, runEnergy: reader.readU16() };
}

export interface PlayerStatePacket {
  name: string;
  pos: WorldPos;
  /** Experience per skill, indexed by the Skill enum. */
  experience: number[];
  levels: number[];
  runEnergy: number;
  running: boolean;
  combatLevel: number;
  /** What this character looks like, so their own body is drawn as theirs. */
  look: EntityLook;
}

export function encodePlayerState(
  packet: PlayerStatePacket,
  originX: number,
  originZ: number,
): Uint8Array {
  const writer = new ByteWriter(256);
  writer.writeU8(ServerOp.PlayerState).writeString(packet.name);
  writePosition(writer, packet.pos, originX, originZ);
  writer.writeU8(packet.experience.length);
  for (let i = 0; i < packet.experience.length; i++) {
    writer.writeVarInt(packet.experience[i] ?? 0).writeU8(packet.levels[i] ?? 1);
  }
  writer
    .writeU16(Math.round(packet.runEnergy))
    .writeBool(packet.running)
    .writeU16(packet.combatLevel);
  writeLook(writer, packet.look);
  return writer.toUint8Array();
}

export function decodePlayerState(
  reader: ByteReader,
  originX: number,
  originZ: number,
): PlayerStatePacket {
  const name = reader.readString(32);
  const pos = readPosition(reader, originX, originZ);
  const count = reader.readU8();
  const experience = new Array<number>(count);
  const levels = new Array<number>(count);
  for (let i = 0; i < count; i++) {
    experience[i] = reader.readVarInt();
    levels[i] = reader.readU8();
  }
  const runEnergy = reader.readU16();
  const running = reader.readBool();
  const combatLevel = reader.readU16();
  return {
    name,
    pos,
    experience,
    levels,
    runEnergy,
    running,
    combatLevel,
    look: readLook(reader),
  };
}

/**
 * Where a line was said, which is what decides who hears it and how it is drawn.
 *
 * The scope rides with every line rather than being worked out from who sent it,
 * because the same person says things in several of these and a client that had
 * to guess would guess wrong on the first system message.
 */
export const ChatScope = {
  /** The world talking: what was picked up, what a door said, what went wrong. */
  System: 0,
  /** Said out loud, heard by whoever is near enough to see it happen. */
  Nearby: 1,
  /** Said to everybody who is logged in. */
  World: 2,
  /** Said to a guild. */
  Guild: 3,
  /** Said to one person, or heard from one. */
  Private: 4,
} as const;

export type ChatScopeValue = (typeof ChatScope)[keyof typeof ChatScope];

export interface ChatPacket {
  entityId: number;
  name: string;
  message: string;
  scope: ChatScopeValue;
}

export function encodeChatMessage(packet: ChatPacket): Uint8Array {
  const writer = new ByteWriter(192);
  writer
    .writeU8(ServerOp.Chat)
    .writeU32(packet.entityId)
    .writeString(packet.name)
    .writeString(packet.message)
    .writeU8(packet.scope);
  return writer.toUint8Array();
}

export function decodeChatMessage(reader: ByteReader): ChatPacket {
  return {
    entityId: reader.readU32(),
    name: reader.readString(32),
    message: reader.readString(256),
    scope: reader.readU8() as ChatScopeValue,
  };
}

/**
 * A window opened for one player by something a character said.
 *
 * Named rather than numbered, because what is opened is a part of the game
 * rather than a thing somebody made in the tools.
 */
/**
 * Saying something, and where.
 *
 * The scope is sent rather than worked out from a prefix on the text, so a line
 * that begins with a slash is a line about a slash and nothing has to be parsed
 * twice by two different pieces of code that will one day disagree.
 */
export interface SayPacket {
  scope: ChatScopeValue;
  message: string;
}

export function encodeSay(packet: SayPacket): Uint8Array {
  return new ByteWriter(320)
    .writeU8(ClientOp.Chat)
    .writeU8(packet.scope)
    .writeString(packet.message)
    .toUint8Array();
}

export function decodeSay(reader: ByteReader): SayPacket {
  return { scope: reader.readU8() as ChatScopeValue, message: reader.readString(256) };
}

export interface OpenMenuPacket {
  menu: string;
}

export function encodeOpenMenu(packet: OpenMenuPacket): Uint8Array {
  return new ByteWriter(32).writeU8(ServerOp.OpenMenu).writeString(packet.menu).toUint8Array();
}

export function decodeOpenMenu(reader: ByteReader): OpenMenuPacket {
  return { menu: reader.readString(24) };
}

/** What somebody chose to look like. Colours only: the rest is worn. */
export interface SetLookPacket {
  skin: number;
  eyes: number;
}

export function encodeSetLook(packet: SetLookPacket): Uint8Array {
  return new ByteWriter(12)
    .writeU8(ClientOp.SetLook)
    .writeU32(packet.skin >>> 0)
    .writeU32(packet.eyes >>> 0)
    .toUint8Array();
}

export function decodeSetLook(reader: ByteReader): SetLookPacket {
  return { skin: reader.readU32(), eyes: reader.readU32() };
}

/**
 * The things that run down and come back on their own.
 *
 * Run energy, and mana. Health is not here: it arrives with everything else
 * about a character because a blow landing has to say how much it took, and a
 * second packet saying the same thing a moment later is a second chance for the
 * two to disagree.
 */
export interface VitalsPacket {
  runEnergy: number;
  mana: number;
  maxMana: number;
  /**
   * Whether run is switched on, as the server has it.
   *
   * Running switches itself off at no energy, and the client has to hear about
   * it when the server got there first: otherwise the next walk is predicted at
   * a run and walked at a walk, and dragged back all the way.
   */
  running: boolean;
}

export function encodeVitals(packet: VitalsPacket): Uint8Array {
  return new ByteWriter(8)
    .writeU8(ServerOp.VitalsUpdate)
    .writeU16(Math.round(packet.runEnergy))
    .writeU16(Math.round(packet.mana))
    .writeU16(Math.round(packet.maxMana))
    .writeBool(packet.running)
    .toUint8Array();
}

export function decodeVitals(reader: ByteReader): VitalsPacket {
  return {
    runEnergy: reader.readU16(),
    mana: reader.readU16(),
    maxMana: reader.readU16(),
    running: reader.readBool(),
  };
}

/** Whether somebody hits back on their own. */
export function encodeSetRetaliate(on: boolean): Uint8Array {
  return new ByteWriter(2).writeU8(ClientOp.SetRetaliate).writeBool(on).toUint8Array();
}

export function decodeSetRetaliate(reader: ByteReader): boolean {
  return reader.readBool();
}

/** Which stance somebody fights in. Matches MeleeStance in the shared package. */
export function encodeSetStance(stance: number): Uint8Array {
  return new ByteWriter(2).writeU8(ClientOp.SetStance).writeU8(stance).toUint8Array();
}

export function decodeSetStance(reader: ByteReader): number {
  return reader.readU8();
}

/**
 * A blow landed on somebody.
 *
 * Zero damage is a blow that was blocked, which reads differently on screen and
 * is worth sending: a fight where nothing appears to happen looks broken.
 */
export interface HitPacket {
  entityId: number;
  damage: number;
  /** Health left afterwards, and what full health is, for the bar over their head. */
  health: number;
  maxHealth: number;
  /** Whose blow it was, so the one who struck can be shown swinging. */
  fromId: number;
}

export function encodeHit(packet: HitPacket): Uint8Array {
  return new ByteWriter(20)
    .writeU8(ServerOp.Hit)
    .writeU32(packet.entityId)
    .writeU16(Math.max(0, Math.round(packet.damage)))
    .writeU16(Math.max(0, Math.round(packet.health)))
    .writeU16(Math.max(1, Math.round(packet.maxHealth)))
    .writeU32(packet.fromId)
    .toUint8Array();
}

export function decodeHit(reader: ByteReader): HitPacket {
  return {
    entityId: reader.readU32(),
    damage: reader.readU16(),
    health: reader.readU16(),
    maxHealth: reader.readU16(),
    fromId: reader.readU32(),
  };
}

/** Somebody died. The body falls over and is taken away a moment later. */
export function encodeDied(entityId: number): Uint8Array {
  return new ByteWriter(8).writeU8(ServerOp.Died).writeU32(entityId).toUint8Array();
}

export function decodeDied(reader: ByteReader): number {
  return reader.readU32();
}

export function encodeSystemUpdate(seconds: number): Uint8Array {
  return new ByteWriter(4).writeU8(ServerOp.SystemUpdate).writeU16(seconds).toUint8Array();
}

export function encodeDisconnect(reason: number): Uint8Array {
  return new ByteWriter(2).writeU8(ServerOp.Disconnect).writeU8(reason).toUint8Array();
}

// ---------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------

export interface NpcDefinitionWire {
  id: number;
  name: string;
  examine: string;
  combatLevel: number;
  actions: string[];
  /**
   * What its menu entries do, as the tools wrote them.
   *
   * Carried as written text for the same reason scenery carries its own: a
   * handful of entries, sent once at login, and a wire format of its own would
   * be another place to keep in step for nothing.
   */
  acts: string;
  /** What sort of character it is, which is how the tools sort them. */
  category: string;
}

export interface ObjectDefinitionWire {
  id: number;
  name: string;
  examine: string;
  actions: string[];
  /** How it stops movement: a whole tile, one edge of one, or not at all. */
  barrier: 'solid' | 'wall' | 'open';
  /** Something built rather than found, which is how the browser sorts it. */
  piece: boolean;
  /** Laid on the ground and stood on: the pointer lands on it, not on the ground. */
  floor: boolean;
  /** Part of the water, and reached from a side rather than from a corner. */
  afloat: boolean;
  /**
   * Whether it stops a shot. Walls do; fences, rocks and trees do not.
   *
   * Sent so the client can predict where a fight at a distance is walked to.
   */
  blocksSight: boolean;
  /**
   * Something a skill is worked at.
   *
   * Said outright rather than guessed from the words on its menu. Working out
   * that Chop means gathering and Net does not is how a fishing spot ended up
   * lit the colour of a door.
   */
  gathered: boolean;
  /**
   * Which of its menu entries are ways of working it.
   *
   * So a client can tell a way that a particular one has been told not to
   * offer from an ordinary menu entry it should always show.
   */
  gatherActions: string[];
  /** The kind of thing it is one of, and which one, for the browser's tiers. */
  family: string;
  tier: string;
  /** Drawn overhead, and taken away while somebody is under it. */
  roof: boolean;
  /** Whose shape it borrows, or zero for its own. */
  looksLike: number;
  /** Which of a tile's layers it belongs to: floor, standing, roof or wall. */
  layer: 'floor' | 'standing' | 'roof' | 'wall';
  /**
   * What it is built out of, when it was built in the tools rather than borrowed.
   *
   * Carried as written text: it is a handful of numbers per part, it is only
   * ever sent once at login, and giving it a wire format of its own would be
   * three more places to keep in step for nothing.
   */
  parts: string;
  /**
   * What its menu entries do, as the tools wrote them.
   *
   * Carried as written text for the same reason the parts are: it is a handful
   * of entries per object, it is only ever sent once at login, and a wire
   * format of its own would be another place to keep in step for nothing. The
   * editor reads it, and the world runs it from its own copy rather than this
   * one.
   */
  acts: string;
  /** The region a door leads into, played as a copy. Empty for anything else. */
  dungeon: string;
  /** The region a door leads into, shared with everybody. Empty for anything else. */
  shared: string;
  /** A way back out of a copy, to wherever its door was. */
  exit: boolean;
  /**
   * The other half of a door, or nought for anything that does not swing.
   *
   * Sent so the tools can show one entry for a door rather than two: open and
   * shut are two states of one thing, and a browser that lists both is a
   * browser that invites somebody to build half a door.
   */
  opensTo: number;
  /** How long it stays swung before it goes back, in ticks. Nought stays. */
  swingsBack: number;
  /** How many tiles it takes across and along, before it is turned. */
  width: number;
  height: number;
  /** A way up to the floor above or down to the one below, or neither. */
  climb: '' | 'up' | 'down';
}

const LAYERS = ['floor', 'standing', 'roof', 'wall'] as const;

const BARRIERS = ['open', 'solid', 'wall'] as const;

const CLIMBS = ['', 'up', 'down'] as const;

export interface ItemDefinitionWire {
  /** What it takes to gather this, and what it gives. Nought for anything not gathered. */
  gatherLevel: number;
  gatherExperience: number;
  /**
   * How many of these come out of one spot before it is spent.
   *
   * A fact about the material rather than about the place: a pine gives an
   * armful and an ironwood gives one log and a sore back. Both nought means one
   * and done, which is what everything written before this meant.
   */
  gatherLeast: number;
  gatherMost: number;
  id: number;
  name: string;
  examine: string;
  /** Many share one slot and are drawn with a count. */
  stackable: boolean;
  /** Equipment slot index, or -1 for anything that cannot be worn. */
  equipSlot: number;
  actions: string[];
  /** Placeholder art key until real icons exist. */
  model: string;
  /** What sort of thing it is, which is what the bank's tabs sort by. */
  kind: string;
  /**
   * What it is worth, what it weighs, what it puts back, and what it gives.
   *
   * Sent so the client can say what an item does without asking: a tooltip that
   * has to wait for a round trip is a tooltip that arrives after the pointer has
   * moved on.
   */
  value: number;
  weight: number;
  heals: number;
  /** Ticks between swings, for a weapon. Zero for anything else. */
  speed: number;
  /** The fourteen bonuses, in the order the panels show them. */
  bonuses: number[];
  /** What it asks of whoever wears it, as `attack 20` lines. */
  needs: string;
  /**
   * What it is for, in the words the tools sort by.
   *
   * Sent so a picker in the tools can offer the four fishing tools rather than
   * every item in the world.
   */
  tags: string[];
}

export interface DefinitionsPacket {
  npcs: NpcDefinitionWire[];
  objects: ObjectDefinitionWire[];
  items: ItemDefinitionWire[];
  /** Models somebody brought in, and how each one sits on a tile. */
  models: ModelWire[];
  /** Everything that can be made, and where. */
  recipes: RecipeWire[];
  /** Quests, so the journal can name what somebody is part way through. */
  quests: QuestWire[];
  /** The shops there are, named, for the tools to offer one where a shop is wanted. */
  shops: { id: number; name: string }[];
  /**
   * What is gathered where: which scenery gives which item.
   *
   * Only the pairing, not the levels or the odds, because this is here so that
   * a quest step saying to chop a tree can be pointed at the nearest tree that
   * gives that log. What a spot is worth is the tools' business and stays on
   * the packet the tools ask for.
   */
  yields: { objectId: number; itemId: number }[];
  /**
   * Which clip a body plays for each thing it can be doing.
   *
   * Only the ones somebody has written down: a pose that is not here is played
   * from the clip the engine was built with, so an empty list is the game
   * exactly as it was.
   */
  poses: { pose: string; clip: string; loops: boolean; speed: number }[];
}

export interface ModelWire {
  name: string;
  title: string;
  file: string;
  scale: number;
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  turns: number;
  tiles: number;
  /** Where it may be used: scenery, object, item, character. */
  tags: string[];
}

/**
 * A recipe, as the client needs it.
 *
 * Sent with the definitions so a crafting window can be built without asking:
 * what it makes, out of what, where, and what it asks of whoever is making it.
 */
export interface RecipeWire {
  /** The station it happens at, or zero for something done in the bag. */
  objectId: number;
  action: string;
  skill: string;
  level: number;
  inputId: number;
  inputQuantity: number;
  outputId: number;
  outputQuantity: number;
  experience: number;
  /** Other skills it takes, on top of the one it trains. */
  alsoNeeds: { skill: string; level: number }[];
  /** Other places the same thing can be made. */
  alsoAt: number[];
  /** Whether the skill guide leaves it out. */
  hidden: boolean;
  /**
   * What has to have happened before it can be made at all.
   *
   * Sent as what it asks rather than as an answer, because whether it holds is
   * a question about the player looking at the window, and the client already
   * knows how far along its own quests are.
   */
  locked: { quest: string; atLeast: number; flag: string; is: string } | null;
}

export interface QuestWire {
  id: string;
  name: string;
  summary: string;
  steps: QuestStepWire[];
  /** How somebody starts it, for a journal that lists quests nobody has begun. */
  hint: string;
  /**
   * What it asks of somebody, written by hand and worked out from its steps.
   *
   * Both are worked out where the quest is: the client shows them, and what a
   * step asks for is a question about conditions rather than about the journal.
   */
  requirements: string[];
  derived: string[];
  /**
   * What has to be true before the journal mentions it, as written text.
   *
   * The conditions themselves rather than an answer, because whether they hold
   * is a question about the player reading the journal, and that is the client.
   */
  hiddenUntil: string;
  /**
   * Whether there is more than one road through it.
   *
   * The journal lists the steps somebody has been through, which is only true
   * of a quest that runs in a line. A quest that forks is one where the step
   * before this one is a guess, and a journal that guesses is a journal that
   * says somebody did something they never did.
   */
  forks: boolean;
  /**
   * What the notice says when it is finished, and what it gave.
   *
   * Both are written by hand rather than derived: an ending is a piece of
   * writing, and a reward reads better as the words whoever wrote the quest
   * chose than as a list of item ids worked out from its effects.
   */
  finishedText: string;
  rewards: { text: string; itemId: number; quantity: number }[];
  /** Whether taking it on follows it without being asked. */
  followed: boolean;
}

/**
 * One step, and where it points.
 *
 * The mark is what the client draws for whoever has this quest pinned: a ring
 * over a character, a piece of scenery or a tile. Zero means it points at
 * nothing of that kind, which is the ordinary case for most steps.
 */
export interface QuestStepWire {
  text: string;
  npcId: number;
  objectId: number;
  itemId: number;
  tx: number;
  tz: number;
  plane: number;
  note: string;
  /**
   * What has to be carried before the step can finish, and how many.
   *
   * Sent so a mark can point at where to get it while it is still wanted, and
   * at whoever asked for it once it is in the bag. Nought for a step that asks
   * for nothing.
   */
  needsItem: number;
  needsCount: number;
  /**
   * A deed the step waits on, and what it was to be done to.
   *
   * Sent for the same reason as the item above: a step that waits for a log to
   * be chopped should put its mark on the nearest tree, and the only way to
   * know that is to know what the step is actually asking for.
   */
  deed: string;
  deedOf: number;
  /**
   * Everything the step asks somebody to hold, and where it has to end up.
   *
   * The bag, a gear slot, or the belt. One step can ask for several: a quest
   * that says to come back with a hatchet in hand and a tinderbox on your belt
   * is one step, not three, and the interface has to be able to say which of
   * them is still missing and mark the ones already in the bag.
   */
  wants: QuestWantWire[];
}

/** One thing a step asks for, and where it has to be. */
export interface QuestWantWire {
  itemId: number;
  count: number;
  /** Where it has to end up: carried, worn as gear, or on the belt. */
  where: QuestWantPlace;
}

export const QuestWantPlace = {
  Carried: 0,
  Worn: 1,
  Belt: 2,
} as const;

export type QuestWantPlace = (typeof QuestWantPlace)[keyof typeof QuestWantPlace];

function readAlsoNeeds(reader: ByteReader): { skill: string; level: number }[] {
  const count = reader.readU8();
  const held = new Array<{ skill: string; level: number }>(count);
  for (let i = 0; i < count; i++) {
    held[i] = { skill: reader.readString(24), level: reader.readU8() };
  }
  return held;
}

function readAlsoAt(reader: ByteReader): number[] {
  const count = reader.readU8();
  const held = new Array<number>(count);
  for (let i = 0; i < count; i++) held[i] = reader.readVarInt();
  return held;
}

function readWants(reader: ByteReader): QuestWantWire[] {
  const count = reader.readU8();
  const wants = new Array<QuestWantWire>(count);
  for (let i = 0; i < count; i++) {
    wants[i] = {
      itemId: reader.readVarInt(),
      count: reader.readVarInt(),
      where: (reader.readU8() as QuestWantPlace) ?? QuestWantPlace.Carried,
    };
  }
  return wants;
}

function writeActions(writer: ByteWriter, actions: readonly string[]): void {
  writer.writeU8(Math.min(actions.length, 8));
  for (let i = 0; i < Math.min(actions.length, 8); i++) {
    writer.writeString(actions[i] ?? '');
  }
}

function readActions(reader: ByteReader): string[] {
  const count = reader.readU8();
  const actions = new Array<string>(count);
  for (let i = 0; i < count; i++) actions[i] = reader.readString(24);
  return actions;
}

export function encodeDefinitions(packet: DefinitionsPacket): Uint8Array {
  const writer = new ByteWriter(4096);
  writer.writeU8(ServerOp.Definitions);

  writer.writeVarInt(packet.npcs.length);
  for (const npc of packet.npcs) {
    writer
      .writeVarInt(npc.id)
      .writeString(npc.name)
      .writeString(npc.examine)
      .writeU16(npc.combatLevel)
      .writeString(npc.category)
      .writeString(npc.acts);
    writeActions(writer, npc.actions);
  }

  writer.writeVarInt(packet.objects.length);
  for (const object of packet.objects) {
    writer.writeVarInt(object.id).writeString(object.name).writeString(object.examine);
    writeActions(writer, object.actions);
    writer
      .writeU8(Math.max(0, BARRIERS.indexOf(object.barrier)))
      .writeBool(object.piece)
      .writeBool(object.floor)
      .writeBool(object.roof)
      .writeBool(object.afloat)
      .writeBool(object.gathered)
      .writeString(object.gatherActions.join(','))
      .writeString(object.family)
      .writeString(object.tier)
      .writeVarInt(object.looksLike)
      .writeU8(Math.max(0, LAYERS.indexOf(object.layer)))
      .writeString(object.parts)
      .writeString(object.dungeon)
      .writeString(object.shared)
      .writeBool(object.exit)
      .writeVarInt(object.opensTo)
      .writeVarInt(object.swingsBack)
      .writeString(object.acts)
      .writeBool(object.blocksSight)
      .writeU8(object.width)
      .writeU8(object.height)
      .writeU8(object.climb === 'up' ? 1 : object.climb === 'down' ? 2 : 0);
  }

  writer.writeVarInt(packet.models.length);
  for (const model of packet.models) {
    writer
      .writeString(model.name)
      .writeString(model.title)
      .writeString(model.file)
      .writeF32(model.scale)
      .writeF32(model.offsetX)
      .writeF32(model.offsetY)
      .writeF32(model.offsetZ)
      .writeU8(model.turns)
      .writeU8(model.tiles)
      .writeString(model.tags.join(','));
  }

  writer.writeVarInt(packet.recipes.length);
  for (const recipe of packet.recipes) {
    writer
      .writeVarInt(recipe.objectId)
      .writeString(recipe.action)
      .writeString(recipe.skill)
      .writeU8(recipe.level)
      .writeVarInt(recipe.inputId)
      .writeVarInt(recipe.inputQuantity)
      .writeVarInt(recipe.outputId)
      .writeVarInt(recipe.outputQuantity)
      .writeVarInt(Math.round(recipe.experience));

    // The other skills it takes, then the gate in front of it. Both are what it
    // asks rather than whether this player passes: the client works that out.
    const also = recipe.alsoNeeds.slice(0, 6);
    writer.writeU8(also.length);
    for (const need of also) writer.writeString(need.skill).writeU8(need.level);

    const places = recipe.alsoAt.slice(0, 8);
    writer.writeU8(places.length);
    for (const objectId of places) writer.writeVarInt(objectId);

    writer.writeBool(recipe.hidden);

    writer.writeBool(recipe.locked !== null);
    if (recipe.locked) {
      writer
        .writeString(recipe.locked.quest)
        .writeVarInt(recipe.locked.atLeast)
        .writeString(recipe.locked.flag)
        .writeString(recipe.locked.is);
    }
  }

  writer.writeVarInt(packet.quests.length);
  for (const quest of packet.quests) {
    writer
      .writeString(quest.id)
      .writeString(quest.name)
      .writeString(quest.summary)
      .writeString(quest.hint)
      .writeString(quest.requirements.join('\n'))
      .writeString(quest.derived.join('\n'))
      .writeString(quest.hiddenUntil)
      .writeBool(quest.forks)
      .writeBool(quest.followed)
      .writeString(quest.finishedText);

    const rewards = quest.rewards.slice(0, 12);
    writer.writeU8(rewards.length);
    for (const reward of rewards) {
      writer.writeString(reward.text).writeVarInt(reward.itemId).writeVarInt(reward.quantity);
    }

    const steps = quest.steps.slice(0, 32);
    writer.writeU8(steps.length);
    for (const step of steps) {
      writer
        .writeString(step.text)
        .writeVarInt(step.npcId)
        .writeVarInt(step.objectId)
        .writeVarInt(step.itemId)
        .writeI32(step.tx)
        .writeI32(step.tz)
        .writeU8(step.plane)
        .writeString(step.note)
        .writeVarInt(step.needsItem)
        .writeVarInt(step.needsCount)
        .writeString(step.deed)
        .writeVarInt(step.deedOf);

      // Capped at what one step can sensibly ask for, so a quest written wrong
      // cannot make a packet nobody can read.
      const wants = step.wants.slice(0, 16);
      writer.writeU8(wants.length);
      for (const want of wants) {
        writer.writeVarInt(want.itemId).writeVarInt(want.count).writeU8(want.where);
      }
    }
  }

  writer.writeVarInt(packet.shops.length);
  for (const shop of packet.shops) writer.writeVarInt(shop.id).writeString(shop.name);

  writer.writeVarInt(packet.yields.length);
  for (const pair of packet.yields) writer.writeVarInt(pair.objectId).writeVarInt(pair.itemId);

  writer.writeU8(Math.min(packet.poses.length, 64));
  for (const pose of packet.poses.slice(0, 64)) {
    writer
      .writeString(pose.pose)
      .writeString(pose.clip)
      .writeBool(pose.loops)
      // Sent as hundredths, since a speed is a small number with a fraction in
      // it and the wire counts in whole numbers.
      .writeVarInt(Math.round(Math.min(4, Math.max(0.1, pose.speed)) * 100));
  }

  writer.writeVarInt(packet.items.length);
  for (const item of packet.items) {
    writer
      .writeVarInt(item.id)
      .writeString(item.name)
      .writeString(item.examine)
      .writeBool(item.stackable)
      .writeI8(item.equipSlot)
      .writeString(item.model)
      .writeString(item.kind)
      .writeVarInt(item.value)
      .writeF32(item.weight)
      .writeVarInt(item.heals)
      .writeU8(item.speed)
      .writeString(item.needs)
      .writeString(item.tags.join(','))
      .writeU8(Math.min(99, Math.max(0, item.gatherLevel)))
      .writeVarInt(Math.round(item.gatherExperience))
      .writeVarInt(Math.max(0, Math.round(item.gatherLeast)))
      .writeVarInt(Math.max(0, Math.round(item.gatherMost)));

    // Fourteen bonuses, always all of them: a missing one and a zero are the same
    // thing to read and one of them costs a length byte.
    for (let at = 0; at < 14; at++) writer.writeI32(item.bonuses[at] ?? 0);

    writeActions(writer, item.actions);
  }

  return writer.toUint8Array();
}

export function decodeDefinitions(reader: ByteReader): DefinitionsPacket {
  const npcCount = reader.readVarInt();
  const npcs = new Array<NpcDefinitionWire>(npcCount);
  for (let i = 0; i < npcCount; i++) {
    npcs[i] = {
      id: reader.readVarInt(),
      name: reader.readString(48),
      examine: reader.readString(160),
      combatLevel: reader.readU16(),
      category: reader.readString(24),
      acts: reader.readString(8000),
      actions: readActions(reader),
    };
  }

  const objectCount = reader.readVarInt();
  const objects = new Array<ObjectDefinitionWire>(objectCount);
  for (let i = 0; i < objectCount; i++) {
    objects[i] = {
      id: reader.readVarInt(),
      name: reader.readString(48),
      examine: reader.readString(160),
      actions: readActions(reader),
      barrier: BARRIERS[reader.readU8()] ?? 'open',
      piece: reader.readBool(),
      floor: reader.readBool(),
      roof: reader.readBool(),
      afloat: reader.readBool(),
      gathered: reader.readBool(),
      gatherActions: reader
        .readString(120)
        .split(',')
        .filter((one) => one.length > 0),
      family: reader.readString(24),
      tier: reader.readString(24),
      looksLike: reader.readVarInt(),
      layer: LAYERS[reader.readU8()] ?? 'standing',
      /*
       * Eight thousand, not two.
       *
       * A part is about a hundred and ten bytes of text and a shape may have
       * twelve of them, so two thousand was already only a little over what a
       * full shape costs. Adding a lean and its direction to every part took
       * another three hundred, and a cap sized just above what is authored
       * today is a cap somebody trips over the week after. See the content
       * caps note: size it several times over.
       */
      parts: reader.readString(8000),
      dungeon: reader.readString(48),
      shared: reader.readString(48),
      exit: reader.readBool(),
      opensTo: reader.readVarInt(),
      swingsBack: reader.readVarInt(),
      acts: reader.readString(8000),
      blocksSight: reader.readBool(),
      width: reader.readU8(),
      height: reader.readU8(),
      climb: CLIMBS[reader.readU8()] ?? '',
    };
  }

  const modelCount = reader.readVarInt();
  const models = new Array<ModelWire>(modelCount);
  for (let i = 0; i < modelCount; i++) {
    models[i] = {
      name: reader.readString(48),
      title: reader.readString(64),
      file: reader.readString(120),
      scale: reader.readF32(),
      offsetX: reader.readF32(),
      offsetY: reader.readF32(),
      offsetZ: reader.readF32(),
      turns: reader.readU8(),
      tiles: reader.readU8(),
      tags: reader
        .readString(64)
        .split(',')
        .filter((tag) => tag.length > 0),
    };
  }

  const recipeCount = reader.readVarInt();
  const recipes = new Array<RecipeWire>(recipeCount);
  for (let i = 0; i < recipeCount; i++) {
    recipes[i] = {
      objectId: reader.readVarInt(),
      action: reader.readString(24),
      skill: reader.readString(24),
      level: reader.readU8(),
      inputId: reader.readVarInt(),
      inputQuantity: reader.readVarInt(),
      outputId: reader.readVarInt(),
      outputQuantity: reader.readVarInt(),
      experience: reader.readVarInt(),
      alsoNeeds: readAlsoNeeds(reader),
      alsoAt: readAlsoAt(reader),
      hidden: reader.readBool(),
      locked: reader.readBool()
        ? {
            quest: reader.readString(48),
            atLeast: reader.readVarInt(),
            flag: reader.readString(48),
            is: reader.readString(48),
          }
        : null,
    };
  }

  const questCount = reader.readVarInt();
  const quests = new Array<QuestWire>(questCount);
  for (let i = 0; i < questCount; i++) {
    const id = reader.readString(48);
    const name = reader.readString(64);
    const summary = reader.readString(300);
    const hint = reader.readString(200);
    const written = reader.readString(1500);
    const worked = reader.readString(1500);
    const hiddenUntil = reader.readString(2000);
    const forks = reader.readBool();
    const followed = reader.readBool();
    const finishedText = reader.readString(300);

    const rewardCount = reader.readU8();
    const rewards: QuestWire['rewards'] = [];
    for (let at = 0; at < rewardCount; at++) {
      rewards.push({
        text: reader.readString(80),
        itemId: reader.readVarInt(),
        quantity: reader.readVarInt(),
      });
    }

    const stepCount = reader.readU8();
    const steps: QuestStepWire[] = [];
    for (let step = 0; step < stepCount; step++) {
      steps.push({
        text: reader.readString(200),
        npcId: reader.readVarInt(),
        objectId: reader.readVarInt(),
        itemId: reader.readVarInt(),
        tx: reader.readI32(),
        tz: reader.readI32(),
        plane: reader.readU8(),
        note: reader.readString(120),
        needsItem: reader.readVarInt(),
        needsCount: reader.readVarInt(),
        deed: reader.readString(24),
        deedOf: reader.readVarInt(),
        wants: readWants(reader),
      });
    }

    quests[i] = {
      id,
      name,
      summary,
      hint,
      requirements: written.length > 0 ? written.split('\n') : [],
      derived: worked.length > 0 ? worked.split('\n') : [],
      hiddenUntil,
      forks,
      followed,
      finishedText,
      rewards,
      steps,
    };
  }

  const shopCount = reader.readVarInt();
  const shops: DefinitionsPacket['shops'] = [];
  for (let i = 0; i < shopCount; i++) {
    shops.push({ id: reader.readVarInt(), name: reader.readString(48) });
  }

  const yieldCount = reader.readVarInt();
  const yields: DefinitionsPacket['yields'] = [];
  for (let i = 0; i < yieldCount; i++) {
    yields.push({ objectId: reader.readVarInt(), itemId: reader.readVarInt() });
  }

  const poseCount = reader.readU8();
  const poses: DefinitionsPacket['poses'] = [];
  for (let i = 0; i < poseCount; i++) {
    poses.push({
      pose: reader.readString(24),
      clip: reader.readString(48),
      loops: reader.readBool(),
      speed: reader.readVarInt() / 100,
    });
  }

  const itemCount = reader.readVarInt();
  const items = new Array<ItemDefinitionWire>(itemCount);
  for (let i = 0; i < itemCount; i++) {
    const id = reader.readVarInt();
    const name = reader.readString(48);
    const examine = reader.readString(160);
    const stackable = reader.readBool();
    const equipSlot = reader.readI8();
    const model = reader.readString(32);
    const kind = reader.readString(16);
    const value = reader.readVarInt();
    const weight = reader.readF32();
    const heals = reader.readVarInt();
    const speed = reader.readU8();
    const needs = reader.readString(64);
    const tags = reader.readString(240);
    const gatherLevel = reader.readU8();
    const gatherExperience = reader.readVarInt();
    const gatherLeast = reader.readVarInt();
    const gatherMost = reader.readVarInt();

    const bonuses: number[] = [];
    for (let at = 0; at < 14; at++) bonuses.push(reader.readI32());

    items[i] = {
      id,
      name,
      examine,
      stackable,
      equipSlot,
      model,
      kind,
      value,
      weight,
      heals,
      speed,
      gatherLevel,
      gatherExperience,
      gatherLeast,
      gatherMost,
      needs,
      tags: tags.length > 0 ? tags.split(',') : [],
      bonuses,
      actions: readActions(reader),
    };
  }

  return { npcs, objects, models, recipes, quests, shops, yields, poses, items };
}

/**
 * Everybody a player has written down, and which of them are here.
 *
 * Names are carried as they are written rather than lowercased, because a list
 * of friends is read by a person. Whether somebody is online is worked out at
 * the moment of sending, so it is a fact about the world rather than a claim
 * the client has to keep up to date.
 */
export interface FriendsPacket {
  /**
   * Each friend, and where they are standing if they are here at all.
   *
   * The place is the area of the world they are in, which is the same name the
   * map in the corner shows them: near enough to be worth reading, and nowhere
   * near enough to say where somebody is hiding.
   */
  friends: { name: string; online: boolean; where: string }[];
  /** Requests waiting to be answered, and ones sent and not yet answered. */
  asking: string[];
  asked: string[];
  ignored: string[];
}

export function encodeFriends(packet: FriendsPacket): Uint8Array {
  const writer = new ByteWriter(1024).writeU8(ServerOp.Friends).writeVarInt(packet.friends.length);

  for (const friend of packet.friends) {
    writer.writeString(friend.name).writeBool(friend.online).writeString(friend.where);
  }

  writer.writeVarInt(packet.ignored.length);
  for (const name of packet.ignored) writer.writeString(name);

  writer.writeVarInt(packet.asking.length);
  for (const name of packet.asking) writer.writeString(name);

  writer.writeVarInt(packet.asked.length);
  for (const name of packet.asked) writer.writeString(name);

  return writer.toUint8Array();
}

export function decodeFriends(reader: ByteReader): FriendsPacket {
  const friends: { name: string; online: boolean; where: string }[] = [];
  const many = reader.readVarInt();
  for (let at = 0; at < many; at++) {
    friends.push({
      name: reader.readString(32),
      online: reader.readBool(),
      where: reader.readString(64),
    });
  }

  const ignored: string[] = [];
  const others = reader.readVarInt();
  for (let at = 0; at < others; at++) ignored.push(reader.readString(32));

  const asking: string[] = [];
  const waiting = reader.readVarInt();
  for (let at = 0; at < waiting; at++) asking.push(reader.readString(32));

  const asked: string[] = [];
  const outstanding = reader.readVarInt();
  for (let at = 0; at < outstanding; at++) asked.push(reader.readString(32));

  return { friends, ignored, asking, asked };
}

export interface PrivateMessagePacket {
  /** True when this player sent it, false when they received it. */
  outgoing: boolean;
  /** The other party. */
  name: string;
  message: string;
}

export function encodePrivateMessage(packet: PrivateMessagePacket): Uint8Array {
  return new ByteWriter(192)
    .writeU8(ServerOp.PrivateMessage)
    .writeBool(packet.outgoing)
    .writeString(packet.name)
    .writeString(packet.message)
    .toUint8Array();
}

export function decodePrivateMessage(reader: ByteReader): PrivateMessagePacket {
  return {
    outgoing: reader.readBool(),
    name: reader.readString(32),
    message: reader.readString(256),
  };
}

/**
 * Slots in one container.
 *
 * A full update replaces everything the client holds for that container, which is
 * what login and opening the bank send. A partial update carries only the slots that
 * changed, so dragging one item across the inventory costs two entries rather than
 * twenty eight.
 *
 * An entry with item id zero means the slot is now empty.
 */
export interface ContainerSlotWire {
  slot: number;
  itemId: number;
  quantity: number;
}

export interface ContainerUpdatePacket {
  container: number;
  full: boolean;
  slots: ContainerSlotWire[];
}

export function encodeContainerUpdate(packet: ContainerUpdatePacket): Uint8Array {
  const writer = new ByteWriter(16 + packet.slots.length * 8);
  writer.writeU8(ServerOp.ContainerUpdate).writeU8(packet.container).writeBool(packet.full);

  writer.writeVarInt(packet.slots.length);
  for (const entry of packet.slots) {
    writer.writeVarInt(entry.slot).writeVarInt(entry.itemId).writeVarInt(entry.quantity);
  }

  return writer.toUint8Array();
}

export function decodeContainerUpdate(reader: ByteReader): ContainerUpdatePacket {
  const container = reader.readU8();
  const full = reader.readBool();
  const count = reader.readVarInt();

  const slots = new Array<ContainerSlotWire>(count);
  for (let i = 0; i < count; i++) {
    slots[i] = {
      slot: reader.readVarInt(),
      itemId: reader.readVarInt(),
      quantity: reader.readVarInt(),
    };
  }

  return { container, full, slots };
}

// ---------------------------------------------------------------------------
// Ground items
// ---------------------------------------------------------------------------

/**
 * A pile of items on a tile.
 *
 * Only piles the receiving player is allowed to see are ever sent, so a pile that
 * still belongs to somebody else simply does not exist as far as this client is
 * concerned. Nothing about ownership travels.
 */
export interface GroundItemWire {
  id: number;
  itemId: number;
  quantity: number;
  tx: number;
  tz: number;
  plane: number;
  /**
   * How high off the ground it lies, for a pile somebody laid on a surface.
   *
   * Dropped loot falls on the floor and has none of this. Loot left on purpose
   * can be left on a table, a shelf or a wall, and a pile drawn on the floor
   * under the table it was meant to be on reads as a pile that fell through.
   */
  lift?: number;
}

export function encodeGroundItems(items: readonly GroundItemWire[]): Uint8Array {
  const writer = new ByteWriter(8 + items.length * 15);
  writer.writeU8(ServerOp.GroundItems).writeVarInt(items.length);

  for (const item of items) {
    writer
      .writeVarInt(item.id)
      .writeVarInt(item.itemId)
      .writeVarInt(item.quantity)
      .writeVarInt(item.tx)
      .writeVarInt(item.tz)
      .writeU8(item.plane)
      // One byte says whether there is a height, which is cheaper across every
      // pile in a city than a height for the thousands that lie on the floor.
      .writeU8(item.lift === undefined ? 0 : 1);

    if (item.lift !== undefined) writer.writeI16(Math.round(item.lift * 32));
  }

  return writer.toUint8Array();
}

export function decodeGroundItems(reader: ByteReader): GroundItemWire[] {
  const count = reader.readVarInt();
  const items = new Array<GroundItemWire>(count);
  for (let i = 0; i < count; i++) {
    const held: GroundItemWire = {
      id: reader.readVarInt(),
      itemId: reader.readVarInt(),
      quantity: reader.readVarInt(),
      tx: reader.readVarInt(),
      tz: reader.readVarInt(),
      plane: reader.readU8(),
    };

    if (reader.readU8() === 1) held.lift = reader.readI16() / 32;
    items[i] = held;
  }
  return items;
}

export function encodeGroundItemsGone(ids: readonly number[]): Uint8Array {
  const writer = new ByteWriter(8 + ids.length * 3);
  writer.writeU8(ServerOp.GroundItemsGone).writeVarInt(ids.length);
  for (const id of ids) writer.writeVarInt(id);
  return writer.toUint8Array();
}

export function decodeGroundItemsGone(reader: ByteReader): number[] {
  const count = reader.readVarInt();
  const ids = new Array<number>(count);
  for (let i = 0; i < count; i++) ids[i] = reader.readVarInt();
  return ids;
}

// ---------------------------------------------------------------------------
// Equipment
// ---------------------------------------------------------------------------

/**
 * A tool into the belt, or back out of it.
 *
 * One byte each way: which slot, and which direction. Which belt slot it lands
 * in is not asked, because it is not a choice: a hatchet goes where hatchets go.
 */
export interface ToolbeltPacket {
  /** True to put the bag slot on the belt, false to take a belt slot off it. */
  onto: boolean;
  slot: number;
}

export function encodeToolbelt(packet: ToolbeltPacket): Uint8Array {
  return new ByteWriter(4)
    .writeU8(ClientOp.Toolbelt)
    .writeU8(packet.onto ? 1 : 0)
    .writeU8(packet.slot)
    .toUint8Array();
}

export function decodeToolbelt(reader: ByteReader): ToolbeltPacket {
  return { onto: reader.readU8() === 1, slot: reader.readU8() };
}

export function encodeUnequip(slot: number): Uint8Array {
  return new ByteWriter(4).writeU8(ClientOp.Unequip).writeU8(slot).toUint8Array();
}

export interface AppearancePacket {
  entityId: number;
  /** Item id per equipment slot, zero for an empty one. */
  equipment: number[];
  /** The model and colours, which change when somebody changes them. */
  look: EntityLook;
}

export function encodeAppearance(packet: AppearancePacket): Uint8Array {
  const writer = new ByteWriter(32);
  writer.writeU8(ServerOp.Appearance).writeU32(packet.entityId).writeU8(packet.equipment.length);
  for (const itemId of packet.equipment) writer.writeVarInt(itemId);
  writeLook(writer, packet.look);
  return writer.toUint8Array();
}

export function decodeAppearance(reader: ByteReader): AppearancePacket {
  const entityId = reader.readU32();
  const count = reader.readU8();
  const equipment = new Array<number>(count);
  for (let i = 0; i < count; i++) equipment[i] = reader.readVarInt();
  return { entityId, equipment, look: readLook(reader) };
}

/**
 * Summed bonuses from what the local player is wearing.
 *
 * Sent rather than derived on the client because the server is the only side that
 * decides what an item is worth, and because the client would otherwise need every
 * bonus of every item just to add up eleven of them.
 */
export interface BonusesPacket {
  attackStab: number;
  attackSlash: number;
  attackBash: number;
  attackRanged: number;
  attackMagic: number;
  defenceStab: number;
  defenceSlash: number;
  defenceBash: number;
  defenceRanged: number;
  defenceMagic: number;
  strength: number;
  rangedStrength: number;
  magicDamage: number;
  prayer: number;
  /**
   * What the numbers above come to in a fight.
   *
   * Worked out by the server, because the server is the only side that decides a
   * blow. Sent with the bonuses so the panel can say what a weapon is worth
   * rather than leaving somebody to wonder why they keep hitting ones.
   */
  maxHit: number;
  /** The attack roll, and the way the weapon is being swung. */
  accuracy: number;
  style: string;
  /** Ticks between blows with what is held. */
  speed: number;
  /** How they are fighting, and whether being hit is answered on its own. */
  stance: number;
  /**
   * Which of the three ways they are fighting: melee, ranged or magic.
   *
   * Sent because the stance the panel shows is one of four in that way, and only
   * the server knows which way what somebody is holding puts them in.
   */
  way: number;
  retaliate: boolean;
  /**
   * How far their blows reach with what they are holding, in tiles.
   *
   * Worked out by the server and sent rather than worked out again, so the walk
   * the client predicts towards something being shot stops where the server's
   * does instead of at arm's length.
   */
  reach: number;
}

/** Field order on the wire. Appended to, never reordered. */
export const BONUS_FIELDS = [
  'attackStab',
  'attackSlash',
  'attackBash',
  'attackRanged',
  'attackMagic',
  'defenceStab',
  'defenceSlash',
  'defenceBash',
  'defenceRanged',
  'defenceMagic',
  'strength',
  'rangedStrength',
  'magicDamage',
  'prayer',
] as const satisfies readonly (keyof BonusesPacket)[];

export function encodeBonuses(packet: BonusesPacket): Uint8Array {
  const writer = new ByteWriter(32 + BONUS_FIELDS.length * 2);
  writer.writeU8(ServerOp.Bonuses);
  for (const field of BONUS_FIELDS) writer.writeI16(packet[field]);

  writer
    .writeU16(Math.max(0, Math.round(packet.maxHit)))
    .writeVarInt(Math.max(0, Math.round(packet.accuracy)))
    .writeString(packet.style)
    .writeU8(Math.max(1, Math.round(packet.speed)))
    .writeU8(Math.max(0, Math.round(packet.stance)))
    .writeU8(Math.min(2, Math.max(0, Math.round(packet.way))))
    .writeBool(packet.retaliate)
    .writeU8(Math.min(255, Math.max(1, Math.round(packet.reach))));

  return writer.toUint8Array();
}

export function decodeBonuses(reader: ByteReader): BonusesPacket {
  const packet = {} as BonusesPacket;
  for (const field of BONUS_FIELDS) packet[field] = reader.readI16();

  packet.maxHit = reader.readU16();
  packet.accuracy = reader.readVarInt();
  packet.style = reader.readString(16);
  packet.speed = reader.readU8();
  packet.stance = reader.readU8();
  packet.way = reader.readU8();
  packet.retaliate = reader.readBool();
  packet.reach = reader.readU8();

  return packet;
}

/**
 * Experience in one skill after it changed.
 *
 * Sent rather than accumulated on the client, so a client that missed a packet or
 * did its own arithmetic cannot end up showing a level the server disagrees with.
 */
export interface SkillUpdatePacket {
  skill: number;
  experience: number;
  level: number;
}

export function encodeSkillUpdate(packet: SkillUpdatePacket): Uint8Array {
  return new ByteWriter(16)
    .writeU8(ServerOp.SkillUpdate)
    .writeU8(packet.skill)
    .writeVarInt(Math.round(packet.experience))
    .writeU8(packet.level)
    .toUint8Array();
}

export function decodeSkillUpdate(reader: ByteReader): SkillUpdatePacket {
  return { skill: reader.readU8(), experience: reader.readVarInt(), level: reader.readU8() };
}

// ---------------------------------------------------------------------------
// Bank and shops
// ---------------------------------------------------------------------------

export interface BankMovePacket {
  /** True to put something in, false to take it out. */
  deposit: boolean;
  /** Slot in the bag when depositing, slot in the bank when withdrawing. */
  slot: number;
  /** How many. Zero means everything in that slot. */
  quantity: number;
  /**
   * Where it comes from or goes to.
   *
   * Ordinarily the bag. Depositing from `worn` empties a slot of the kit
   * straight into the bank, and withdrawing to `worn` puts it on, swapping out
   * whatever was already there.
   */
  kit: boolean;
  /**
   * Everything of that kind rather than one slot of it.
   *
   * Things that do not stack sit in a slot each, and asking for ten of them
   * means ten slots. The count is what somebody asked for; this says to look
   * beyond the slot they asked from.
   */
  every: boolean;
}

export function encodeBankMove(packet: BankMovePacket): Uint8Array {
  return new ByteWriter(16)
    .writeU8(ClientOp.BankMove)
    .writeBool(packet.deposit)
    .writeVarInt(packet.slot)
    .writeVarInt(packet.quantity)
    .writeBool(packet.kit)
    .writeBool(packet.every)
    .toUint8Array();
}

export function decodeBankMove(reader: ByteReader): BankMovePacket {
  return {
    deposit: reader.readBool(),
    slot: reader.readVarInt(),
    quantity: reader.readVarInt(),
    kit: reader.readBool(),
    every: reader.readBool(),
  };
}

export function encodeBankState(open: boolean): Uint8Array {
  return new ByteWriter(4).writeU8(ServerOp.BankState).writeBool(open).toUint8Array();
}

export interface ShopTradePacket {
  /** True to buy from the shop, false to sell to it. */
  buying: boolean;
  /** Shop slot when buying, inventory slot when selling. */
  slot: number;
  quantity: number;
}

export function encodeShopTrade(packet: ShopTradePacket): Uint8Array {
  return new ByteWriter(12)
    .writeU8(ClientOp.ShopTrade)
    .writeBool(packet.buying)
    .writeVarInt(packet.slot)
    .writeVarInt(packet.quantity)
    .toUint8Array();
}

export function decodeShopTrade(reader: ByteReader): ShopTradePacket {
  return {
    buying: reader.readBool(),
    slot: reader.readVarInt(),
    quantity: reader.readVarInt(),
  };
}

export function encodeCloseInterface(): Uint8Array {
  return new ByteWriter(2).writeU8(ClientOp.CloseInterface).toUint8Array();
}

/**
 * What a shop is selling.
 *
 * Prices are sent rather than derived, because the shop decides them and a client
 * working them out from an item's value would only be right until the first shop
 * charged something different.
 */
export interface ShopEntryWire {
  itemId: number;
  quantity: number;
  /** What it costs to buy one. */
  price: number;
  /** What the shop pays for one. */
  offer: number;
  /**
   * Whether this line is out of reach for whoever is looking at it.
   *
   * Sent rather than hidden, and sent in its own place in the list rather than
   * left out, so what a shop keeps for the people who have earned it is
   * something everybody else can see themselves working towards, and so the
   * line somebody presses is the line the world thinks they pressed.
   */
  locked: boolean;
}

export interface ShopStatePacket {
  open: boolean;
  name: string;
  entries: ShopEntryWire[];
}

export function encodeShopState(packet: ShopStatePacket): Uint8Array {
  const writer = new ByteWriter(64 + packet.entries.length * 12);
  writer.writeU8(ServerOp.ShopState).writeBool(packet.open).writeString(packet.name);

  writer.writeVarInt(packet.entries.length);
  for (const entry of packet.entries) {
    writer
      .writeVarInt(entry.itemId)
      .writeVarInt(entry.quantity)
      .writeVarInt(entry.price)
      .writeVarInt(entry.offer)
      .writeBool(entry.locked);
  }

  return writer.toUint8Array();
}

export function decodeShopState(reader: ByteReader): ShopStatePacket {
  const open = reader.readBool();
  const name = reader.readString(48);
  const count = reader.readVarInt();

  const entries = new Array<ShopEntryWire>(count);
  for (let i = 0; i < count; i++) {
    entries[i] = {
      itemId: reader.readVarInt(),
      quantity: reader.readVarInt(),
      price: reader.readVarInt(),
      offer: reader.readVarInt(),
      locked: reader.readBool(),
    };
  }

  return { open, name, entries };
}

// ---------------------------------------------------------------------------
// Development tools
// ---------------------------------------------------------------------------

/**
 * A command from the tools.
 *
 * Named rather than numbered, and carrying its arguments as a small record, so
 * adding a power is adding a case on the server rather than a packet. The cost is
 * a few bytes per command, which is nothing for something a person types.
 */
export interface AdminCommandPacket {
  command: string;
  /** Numbers by name: ids, levels, quantities, tiles. */
  numbers: Record<string, number>;
  /** Text by name: player names, item names, region ids. */
  text: Record<string, string>;
}

/**
 * The longest one named piece of text may be.
 *
 * Read from and written to both sides, so a value that fits here fits the
 * socket as well: the gateway allows a tools packet comfortably more than this.
 */
export const RECORD_TEXT_MAX = 96_000;

function writeRecord(writer: ByteWriter, record: Record<string, string>): void {
  const entries = Object.entries(record).slice(0, 16);
  writer.writeU8(entries.length);
  for (const [key, value] of entries) writer.writeString(key).writeString(value);
}

/**
 * Named text, both ways.
 *
 * The limit is generous because one of the things carried this way is a whole
 * conversation, which the tools edit as a lump rather than through a packet of
 * its own for something only an editor ever asks about.
 */
function readRecord(reader: ByteReader): Record<string, string> {
  const count = reader.readU8();
  const record: Record<string, string> = {};

  // Generous, because one of these carries a whole conversation as JSON and a
  // conversation worth writing is longer than a few lines. Ardor's ran to
  // twenty one thousand bytes while the tutorial was being written, which is
  // ordinary for a character somebody actually talks to, so the room here is
  // for a conversation several times that. The socket itself holds the real
  // limit.
  for (let i = 0; i < count; i++)
    record[reader.readString(32)] = reader.readString(RECORD_TEXT_MAX);
  return record;
}

export function encodeAdminCommand(packet: AdminCommandPacket): Uint8Array {
  const writer = new ByteWriter(256);
  writer.writeU8(ClientOp.AdminCommand).writeString(packet.command);

  const numbers = Object.entries(packet.numbers).slice(0, 16);
  writer.writeU8(numbers.length);
  for (const [key, value] of numbers) writer.writeString(key).writeF64(value);

  writeRecord(writer, packet.text);
  return writer.toUint8Array();
}

export function decodeAdminCommand(reader: ByteReader): AdminCommandPacket {
  const command = reader.readString(32);

  const count = reader.readU8();
  const numbers: Record<string, number> = {};
  for (let i = 0; i < count; i++) numbers[reader.readString(32)] = reader.readF64();

  return { command, numbers, text: readRecord(reader) };
}

/** One thing the editor has hold of. */
export interface EditorPickWire {
  tx: number;
  tz: number;
  id: number;
  rotation: number;
  /**
   * How big it was built, and how high it was built to.
   *
   * Carried because a copy that comes back the size it was modelled at is not a
   * copy: a tree scaled up and a dock laid level are both work somebody did,
   * and pasting used to throw both away. Left out means whatever the shape says.
   */
  scale?: number;
  lift?: number;
}

export interface EditorActionPacket {
  /** move, turn, scale, vary, remove, replace, or paste. */
  action: string;
  /** A tile offset for a move, or where a paste lands. */
  tx: number;
  tz: number;
  /** Quarter turns for a turn, sixteenths for a scale. */
  value: number;
  picks: EditorPickWire[];
}

export function encodeEditorAction(packet: EditorActionPacket): Uint8Array {
  const writer = new ByteWriter(64 + packet.picks.length * 20);
  writer
    .writeU8(ClientOp.EditorAction)
    .writeString(packet.action)
    .writeI32(packet.tx)
    .writeI32(packet.tz)
    .writeI32(packet.value);

  const picks = packet.picks.slice(0, 1024);
  writer.writeVarInt(picks.length);
  for (const pick of picks) {
    writer.writeI32(pick.tx).writeI32(pick.tz).writeVarInt(pick.id).writeU8(pick.rotation);

    // Size in hundredths, and a flag for a height of its own, so the common
    // case of neither costs two bytes rather than five.
    writer.writeVarInt(Math.round(Math.min(4, Math.max(0, pick.scale ?? 0)) * 100));
    writer.writeU8(pick.lift === undefined ? 0 : 1);
    if (pick.lift !== undefined) writer.writeF32(pick.lift);
  }

  return writer.toUint8Array();
}

export function decodeEditorAction(reader: ByteReader): EditorActionPacket {
  const action = reader.readString(24);
  const tx = reader.readI32();
  const tz = reader.readI32();
  const value = reader.readI32();

  const count = reader.readVarInt();
  const picks = new Array<EditorPickWire>(count);
  for (let i = 0; i < count; i++) {
    const tile = { tx: reader.readI32(), tz: reader.readI32() };
    const id = reader.readVarInt();
    const rotation = reader.readU8();
    const scale = reader.readVarInt() / 100;
    const lifted = reader.readU8() === 1;

    picks[i] = {
      ...tile,
      id,
      rotation,
      ...(scale > 0 ? { scale } : {}),
      ...(lifted ? { lift: reader.readF32() } : {}),
    };
  }

  return { action, tx, tz, value, picks };
}

export interface AdminStatePacket {
  role: string;
  /** Names of everybody online, for the moderation list. */
  online: string[];
}

export function encodeAdminState(packet: AdminStatePacket): Uint8Array {
  const writer = new ByteWriter(64 + packet.online.length * 20);
  writer.writeU8(ServerOp.AdminState).writeString(packet.role);

  const names = packet.online.slice(0, 255);
  writer.writeU8(names.length);
  for (const name of names) writer.writeString(name);

  return writer.toUint8Array();
}

export function decodeAdminState(reader: ByteReader): AdminStatePacket {
  const role = reader.readString(16);
  const count = reader.readU8();

  const online = new Array<string>(count);
  for (let i = 0; i < count; i++) online[i] = reader.readString(32);

  return { role, online };
}

/**
 * What the server knows about the thing the tools are looking at.
 *
 * Deliberately loose. Every kind of thing the editor can select has a different
 * set of facts about it, and a packet per kind would be a packet per feature.
 */
export interface AdminFactsPacket {
  /** What was asked about: 'spawn', and more as the tools grow. */
  kind: string;
  numbers: Record<string, number>;
  text: Record<string, string>;
}

export function encodeAdminFacts(packet: AdminFactsPacket): Uint8Array {
  const writer = new ByteWriter(256);
  writer.writeU8(ServerOp.AdminFacts).writeString(packet.kind);

  const numbers = Object.entries(packet.numbers).slice(0, 16);
  writer.writeU8(numbers.length);
  for (const [key, value] of numbers) writer.writeString(key).writeF64(value);

  writeRecord(writer, packet.text);
  return writer.toUint8Array();
}

export function decodeAdminFacts(reader: ByteReader): AdminFactsPacket {
  const kind = reader.readString(32);

  const count = reader.readU8();
  const numbers: Record<string, number> = {};
  for (let i = 0; i < count; i++) numbers[reader.readString(32)] = reader.readF64();

  return { kind, numbers, text: readRecord(reader) };
}

/**
 * One line of a drop table.
 *
 * The two halves of a table read as one list here: an entry that happens every
 * kill carries no weight, and the rest are weighted against each other. An item
 * of zero is the entry for nothing, which is how a dry streak is written down.
 */
export interface DropEntryWire {
  itemId: number;
  min: number;
  max: number;
  weight: number;
  everyKill: boolean;
}

export interface DropTablePacket {
  npcId: number;
  entries: DropEntryWire[];
  /**
   * The further rolls, as written.
   *
   * JSON, the same as an act travels, because a roll carries conditions and a
   * condition is a shape the wire has no business knowing twice.
   */
  rolls: string;
}

export function encodeDropTable(packet: DropTablePacket): Uint8Array {
  const writer = new ByteWriter(64 + packet.entries.length * 12 + packet.rolls.length * 2);
  writer.writeU8(ServerOp.DropTable).writeVarInt(packet.npcId);

  const entries = packet.entries.slice(0, 255);
  writer.writeU8(entries.length);
  for (const entry of entries) {
    writer
      .writeU8(entry.everyKill ? 1 : 0)
      .writeVarInt(entry.itemId)
      .writeVarInt(entry.min)
      .writeVarInt(entry.max)
      .writeVarInt(entry.weight);
  }

  writer.writeString(packet.rolls);

  return writer.toUint8Array();
}

export function decodeDropTable(reader: ByteReader): DropTablePacket {
  const npcId = reader.readVarInt();
  const count = reader.readU8();

  const entries = new Array<DropEntryWire>(count);
  for (let i = 0; i < count; i++) {
    entries[i] = {
      everyKill: reader.readU8() === 1,
      itemId: reader.readVarInt(),
      min: reader.readVarInt(),
      max: reader.readVarInt(),
      weight: reader.readVarInt(),
    };
  }

  return { npcId, entries, rolls: reader.readString(16_000) };
}

/**
 * A spawn zone as the tools see it.
 *
 * Tiles travel as world coordinates rather than the region's own, because the
 * editor works in the same numbers the rest of the interface shows.
 */
export interface ZoneWire {
  id: number;
  npcId: number;
  /** What it is called, or empty to be called after what comes back in it. */
  name: string;
  max: number;
  minTicks: number;
  maxTicks: number;
  /** How many it is holding at this moment, which is what says whether it works. */
  alive: number;
  /** Off means it keeps everything about itself but puts nothing out. */
  paused: boolean;
  /**
   * A flag the world holds that has to say something before it spawns.
   *
   * Empty is a zone that waits for nothing, which is nearly all of them.
   */
  needsWorld: string;
  worldIs: string;
  /** The stretch of day it puts anything out in. Empty is any. */
  atTime: string;
  tiles: { tx: number; tz: number }[];
}

export interface ZonesPacket {
  zones: ZoneWire[];
}

export function encodeZones(packet: ZonesPacket): Uint8Array {
  const writer = new ByteWriter(1024);
  writer.writeU8(ServerOp.Zones).writeVarInt(packet.zones.length);

  for (const zone of packet.zones) {
    writer
      .writeVarInt(zone.id)
      .writeVarInt(zone.npcId)
      .writeVarInt(zone.max)
      .writeVarInt(zone.minTicks)
      .writeVarInt(zone.maxTicks)
      .writeVarInt(zone.alive)
      .writeBool(zone.paused)
      .writeString(zone.name)
      .writeString(zone.needsWorld)
      .writeString(zone.worldIs)
      .writeString(zone.atTime)
      .writeVarInt(zone.tiles.length);

    for (const tile of zone.tiles) writer.writeVarInt(tile.tx).writeVarInt(tile.tz);
  }

  return writer.toUint8Array();
}

export function decodeZones(reader: ByteReader): ZonesPacket {
  const count = reader.readVarInt();
  const zones = new Array<ZoneWire>(count);

  for (let i = 0; i < count; i++) {
    const id = reader.readVarInt();
    const npcId = reader.readVarInt();
    const max = reader.readVarInt();
    const minTicks = reader.readVarInt();
    const maxTicks = reader.readVarInt();
    const alive = reader.readVarInt();
    const paused = reader.readBool();
    const name = reader.readString(32);
    const needsWorld = reader.readString(48);
    const worldIs = reader.readString(48);
    const atTime = reader.readString(16);

    const tileCount = reader.readVarInt();
    const tiles = new Array<{ tx: number; tz: number }>(tileCount);
    for (let t = 0; t < tileCount; t++) {
      tiles[t] = { tx: reader.readVarInt(), tz: reader.readVarInt() };
    }

    zones[i] = {
      id,
      npcId,
      name,
      max,
      minTicks,
      maxTicks,
      alive,
      paused,
      needsWorld,
      worldIs,
      atTime,
      tiles,
    };
  }

  return { zones };
}

/** Where one character is stationed, as the tools see it. */
export interface SpawnWire {
  npcId: number;
  tx: number;
  tz: number;
  respawnTicks: number;
  /** Whether it is standing there at this moment. */
  alive: boolean;
}

export interface SpawnsPacket {
  spawns: SpawnWire[];
}

export function encodeSpawns(packet: SpawnsPacket): Uint8Array {
  const writer = new ByteWriter(64 + packet.spawns.length * 10);
  writer.writeU8(ServerOp.Spawns).writeVarInt(packet.spawns.length);

  for (const spawn of packet.spawns) {
    writer
      .writeVarInt(spawn.npcId)
      .writeVarInt(spawn.tx)
      .writeVarInt(spawn.tz)
      .writeVarInt(spawn.respawnTicks)
      .writeBool(spawn.alive);
  }

  return writer.toUint8Array();
}

export function decodeSpawns(reader: ByteReader): SpawnsPacket {
  const count = reader.readVarInt();
  const spawns = new Array<SpawnWire>(count);

  for (let i = 0; i < count; i++) {
    spawns[i] = {
      npcId: reader.readVarInt(),
      tx: reader.readVarInt(),
      tz: reader.readVarInt(),
      respawnTicks: reader.readVarInt(),
      alive: reader.readBool(),
    };
  }

  return { spawns };
}

/** One line of what a shop keeps. */
export interface ShopStockWire {
  itemId: number;
  quantity: number;
  /**
   * Who the line is for, as written.
   *
   * JSON, the same as an act or a drop roll travels, because it carries
   * conditions and the wire has no business knowing that shape twice.
   */
  when: string;
}

export interface ShopStockPacket {
  id: number;
  name: string;
  /** Who or what opens it, so the panel can say where it is. */
  npcId: number;
  objectId: number;
  buyRate: number;
  sellRate: number;
  restockTicks: number;
  stock: ShopStockWire[];
}

export function encodeShopStock(packet: ShopStockPacket): Uint8Array {
  const writer = new ByteWriter(
    128 + packet.stock.reduce((room, line) => room + 16 + line.when.length * 2, 0),
  );
  writer
    .writeU8(ServerOp.ShopStock)
    .writeVarInt(packet.id)
    .writeString(packet.name)
    .writeVarInt(packet.npcId)
    .writeVarInt(packet.objectId)
    // Rates in hundredths, which is finer than anybody prices anything.
    .writeVarInt(Math.round(packet.buyRate * 100))
    .writeVarInt(Math.round(packet.sellRate * 100))
    .writeVarInt(packet.restockTicks);

  const stock = packet.stock.slice(0, 255);
  writer.writeU8(stock.length);
  for (const line of stock) {
    writer.writeVarInt(line.itemId).writeVarInt(line.quantity).writeString(line.when);
  }

  return writer.toUint8Array();
}

export function decodeShopStock(reader: ByteReader): ShopStockPacket {
  const id = reader.readVarInt();
  const name = reader.readString(48);
  const npcId = reader.readVarInt();
  const objectId = reader.readVarInt();
  const buyRate = reader.readVarInt() / 100;
  const sellRate = reader.readVarInt() / 100;
  const restockTicks = reader.readVarInt();

  const count = reader.readU8();
  const stock = new Array<ShopStockWire>(count);
  for (let i = 0; i < count; i++) {
    stock[i] = {
      itemId: reader.readVarInt(),
      quantity: reader.readVarInt(),
      when: reader.readString(4000),
    };
  }

  return { id, name, npcId, objectId, buyRate, sellRate, restockTicks, stock };
}

/** What gathering at one piece of scenery gives. */
export interface GatheringPacket {
  objectId: number;
  action: string;
  skill: string;
  level: number;
  experience: number;
  itemId: number;
  /** Ticks between attempts. */
  interval: number;
  /** Chance at the level it asks for, in hundredths. */
  chance: number;
  /** Any one of these has to be carried. Empty means bare hands. */
  tools: number[];
  /** Everything that can come up here, when it is more than one thing. */
  catches: { itemId: number; weight: number; level: number; experience: number }[];
  /** How the body moves while this is going on: 'none' or 'reach'. */
  animation: string;
  /**
   * Extra experience for working this particular spot, as a percentage.
   *
   * A fact about the place rather than about the work or about what comes out
   * of it, so it arrives with the spot the panel is looking at.
   */
  bonus: number;
}

export function encodeGathering(packet: GatheringPacket): Uint8Array {
  const writer = new ByteWriter(128);
  writer
    .writeU8(ServerOp.Gathering)
    .writeVarInt(packet.objectId)
    .writeString(packet.action)
    .writeString(packet.skill)
    .writeVarInt(packet.level)
    .writeVarInt(Math.round(packet.experience))
    .writeVarInt(packet.itemId)
    .writeVarInt(packet.interval)
    .writeVarInt(Math.round(packet.chance * 100))
    .writeString(packet.animation)
    .writeVarInt(Math.max(0, Math.round(packet.bonus)));

  writer.writeU8(Math.min(255, packet.tools.length));
  for (const tool of packet.tools) writer.writeVarInt(tool);

  writer.writeU8(Math.min(255, packet.catches.length));
  for (const entry of packet.catches) {
    writer
      .writeVarInt(entry.itemId)
      .writeVarInt(Math.round(entry.weight * 10))
      .writeVarInt(entry.level)
      .writeVarInt(Math.round(entry.experience));
  }

  return writer.toUint8Array();
}

export function decodeGathering(reader: ByteReader): GatheringPacket {
  return {
    objectId: reader.readVarInt(),
    action: reader.readString(24),
    skill: reader.readString(24),
    level: reader.readVarInt(),
    experience: reader.readVarInt(),
    itemId: reader.readVarInt(),
    interval: reader.readVarInt(),
    chance: reader.readVarInt() / 100,
    animation: reader.readString(16),
    bonus: reader.readVarInt(),
    tools: readList(reader, () => reader.readVarInt()),
    catches: readList(reader, () => ({
      itemId: reader.readVarInt(),
      weight: reader.readVarInt() / 10,
      level: reader.readVarInt(),
      experience: reader.readVarInt(),
    })),
  };
}

/** A counted run of whatever the reader is told to read. */
function readList<T>(reader: ByteReader, one: () => T): T[] {
  const count = reader.readU8();
  const held: T[] = [];
  for (let at = 0; at < count; at++) held.push(one());

  return held;
}

/**
 * A body doing something, named rather than numbered.
 *
 * Kept short on purpose: the client knows how to play each of these, and adding
 * one is a name here and a clip there.
 */
export const EntityPose = {
  /** Bent over the work with both hands: a net in the water, a fish on a fire. */
  Bend: 1,
  /** Stooping to take something off the ground. */
  PickUp: 2,
} as const;

export type EntityPoseValue = (typeof EntityPose)[keyof typeof EntityPose];

export interface AnimationPacket {
  entityId: number;
  pose: EntityPoseValue;
}

export function encodeAnimation(packet: AnimationPacket): Uint8Array {
  return new ByteWriter(8)
    .writeU8(ServerOp.EntityAnimation)
    .writeVarInt(packet.entityId)
    .writeU8(packet.pose)
    .toUint8Array();
}

export function decodeAnimation(reader: ByteReader): AnimationPacket {
  return {
    entityId: reader.readVarInt(),
    pose: reader.readU8() as EntityPoseValue,
  };
}

/**
 * The places in a region that do something to whoever walks into them.
 *
 * Sent to the tools as written text, one per trigger. What a trigger holds is
 * the same conditions and effects a line of conversation holds, and the editor
 * already knows how to work with those.
 */
export interface TriggersPacket {
  triggers: { id: number; json: string }[];
}

export function encodeTriggers(packet: TriggersPacket): Uint8Array {
  const writer = new ByteWriter(2048);
  writer.writeU8(ServerOp.Triggers).writeVarInt(packet.triggers.length);

  for (const trigger of packet.triggers) {
    writer.writeVarInt(trigger.id).writeString(trigger.json);
  }

  return writer.toUint8Array();
}

export function decodeTriggers(reader: ByteReader): TriggersPacket {
  const count = reader.readVarInt();
  const triggers: TriggersPacket['triggers'] = [];

  for (let at = 0; at < count; at++) {
    triggers.push({ id: reader.readVarInt(), json: reader.readString(4000) });
  }

  return { triggers };
}

/**
 * Where to hold the camera, and for how long.
 *
 * The world carries on while it is held: this stops the camera following, it
 * does not stop anything happening. Whoever is watching can take it back by
 * moving, which is why the client and not the server decides when it ends.
 */
export interface HoldCameraPacket {
  tx: number;
  tz: number;
  /** Tenths of a second. Zero hands the camera straight back. */
  tenths: number;
}

export function encodeHoldCamera(packet: HoldCameraPacket): Uint8Array {
  return new ByteWriter(16)
    .writeU8(ServerOp.HoldCamera)
    .writeI32(packet.tx)
    .writeI32(packet.tz)
    .writeVarInt(packet.tenths)
    .toUint8Array();
}

export function decodeHoldCamera(reader: ByteReader): HoldCameraPacket {
  return { tx: reader.readI32(), tz: reader.readI32(), tenths: reader.readVarInt() };
}

/** What changed about the world. */
export const PatchKind = {
  ObjectAdded: 1,
  ObjectRemoved: 2,
  /** Corner heights and collision for one tile. */
  TileChanged: 3,
  /** What the thing standing on one tile can be worked for. */
  WaysChanged: 4,
} as const;

export type PatchKindValue = (typeof PatchKind)[keyof typeof PatchKind];

export interface WorldPatchPacket {
  /**
   * What the thing at this tile can be worked for, when it differs from its
   * kind. Absent on every patch that is not about that.
   */
  ways?: string[];
  kind: PatchKindValue;
  tx: number;
  tz: number;
  plane: number;
  /** Object definition id for a placement, or the new flags for a tile. */
  value: number;
  rotation: number;
  /** Drawing size for a placement, in sixteenths. Zero means the usual size. */
  scale?: number;
  /** A height of its own for a placement, for anything built out over water. */
  lift?: number;
  /** A shape of its own for a placement. Nought is drawn as its kind is. */
  looks?: number;
  /** What the tile is made of, for a tile change. */
  ground?: number;
  /** Corner heights, north west first, for a tile change. */
  heights: number[];
  /** Where the water surface lies, for a tile change. */
  water?: number;
  /**
   * Which named area the tile belongs to, one past the start, for a tile change.
   *
   * Carried so that redrawing a border in the tools is something everybody
   * standing in the world sees, the same as moving a corner or painting a tile.
   * Without it the only way to see a border move was to reconnect, which meant
   * nobody drawing one could tell whether they had drawn it in the right place.
   */
  area?: number;
}

export function encodeWorldPatch(packet: WorldPatchPacket): Uint8Array {
  const writer = new ByteWriter(48);
  writer
    .writeU8(ServerOp.WorldPatch)
    .writeU8(packet.kind)
    .writeVarInt(packet.tx)
    .writeVarInt(packet.tz)
    .writeU8(packet.plane)
    .writeVarInt(packet.value)
    .writeU8(packet.rotation)
    .writeU8(Math.round(Math.min(Math.max(packet.scale ?? 1, 0.25), 4) * 16))
    .writeU8(packet.ground ?? 0);

  writer.writeU8(packet.heights.length);
  for (const height of packet.heights) writer.writeI16(Math.round(height * 32));
  writer.writeI16(Math.round((packet.water ?? 0) * 32));

  writer.writeU8(packet.lift === undefined ? 0 : 1);
  if (packet.lift !== undefined) writer.writeI16(Math.round(packet.lift * 32));

  // A shape of its own, or nought to be drawn however its kind is drawn.
  writer.writeVarInt(packet.looks ?? 0);

  // What this one can be worked for, when it differs from its kind. Nought is
  // a patch that says nothing about it; anything else is a count one past
  // itself, so a spot offering none of them can be told from one that never
  // said.
  const ways = packet.ways?.slice(0, 8);
  writer.writeU8(ways === undefined ? 0 : Math.max(1, ways.length + 1));
  for (const way of ways ?? []) writer.writeString(way);

  // Which named area it is in, one past the start, or nought for a patch that
  // says nothing about it.
  writer.writeU8(Math.min(255, Math.max(0, packet.area ?? 0)));

  return writer.toUint8Array();
}

export function decodeWorldPatch(reader: ByteReader): WorldPatchPacket {
  const kind = reader.readU8() as PatchKindValue;
  const tx = reader.readVarInt();
  const tz = reader.readVarInt();
  const plane = reader.readU8();
  const value = reader.readVarInt();
  const rotation = reader.readU8();
  const scale = reader.readU8() / 16;
  const ground = reader.readU8();

  const count = reader.readU8();
  const heights = new Array<number>(count);
  for (let i = 0; i < count; i++) heights[i] = reader.readI16() / 32;

  const water = reader.readI16() / 32;
  const lift = reader.readU8() === 1 ? reader.readI16() / 32 : undefined;

  const looks = reader.readVarInt();

  const marked = reader.readU8();
  const ways =
    marked === 0 ? undefined : Array.from({ length: marked - 1 }, () => reader.readString(24));

  const area = reader.readU8();

  return {
    kind,
    tx,
    tz,
    plane,
    value,
    rotation,
    scale,
    ground,
    heights,
    water,
    lift,
    ...(looks > 0 ? { looks } : {}),
    ways,
    ...(area > 0 ? { area } : {}),
  };
}

/**
 * What is wrong with the world, as the server sees it.
 *
 * A world being built by hand accumulates things that are nearly right: a zone
 * drawn over a lake, a shop selling an item that was renamed, a tree nobody can
 * reach. None of them stop the server, which is exactly why they go unnoticed
 * until somebody walks into one. Asking is cheap and the answer is a list.
 */
export interface WorldProblem {
  /** Where to look, or the region's own corner when it is not about a place. */
  tx: number;
  tz: number;
  /** What sort of thing it is: zone, spawn, scenery, shop, drops, gathering. */
  kind: string;
  what: string;
}

export interface WorldCheckPacket {
  problems: WorldProblem[];
  /**
   * Every problem there is, counted by what it says.
   *
   * The list itself stops at two hundred, which is enough to start fixing and
   * not enough to know how big the job is. Seven hundred trees standing in the
   * sea is one line here and two hundred lines there.
   */
  counts: { kind: string; what: string; many: number }[];
}

/** How many problems are worth carrying. Past this it is not a list, it is a mess. */
const CHECK_LIMIT = 200;

export function encodeWorldCheck(packet: WorldCheckPacket): Uint8Array {
  const problems = packet.problems.slice(0, CHECK_LIMIT);

  const counts = packet.counts.slice(0, CHECK_LIMIT);
  const writer = new ByteWriter(64 + (problems.length + counts.length) * 80);
  writer.writeU8(ServerOp.WorldCheck).writeVarInt(problems.length);

  for (const problem of problems) {
    writer
      .writeI32(problem.tx)
      .writeI32(problem.tz)
      .writeString(problem.kind)
      .writeString(problem.what);
  }

  writer.writeVarInt(counts.length);
  for (const one of counts) {
    writer.writeString(one.kind).writeString(one.what).writeVarInt(one.many);
  }

  return writer.toUint8Array();
}

export function decodeWorldCheck(reader: ByteReader): WorldCheckPacket {
  const count = Math.min(reader.readVarInt(), CHECK_LIMIT);
  const problems: WorldProblem[] = [];

  for (let i = 0; i < count; i++) {
    problems.push({
      tx: reader.readI32(),
      tz: reader.readI32(),
      kind: reader.readString(32),
      what: reader.readString(160),
    });
  }

  const many = Math.min(reader.readVarInt(), CHECK_LIMIT);
  const counts: WorldCheckPacket['counts'] = [];
  for (let i = 0; i < many; i++) {
    counts.push({
      kind: reader.readString(32),
      what: reader.readString(160),
      many: reader.readVarInt(),
    });
  }

  return { problems, counts };
}

/**
 * A character talking, and what can be said back.
 *
 * Sent whenever the conversation moves: what they say now, and the answers the
 * player is allowed to give, which are decided by the server since the
 * conditions behind them are the server's business.
 */
export interface DialoguePacket {
  /** Who is talking, for the name on the window. Empty closes it. */
  name: string;
  /** The entity, so the client can look at whoever is speaking. */
  entityId: number;
  text: string;
  /**
   * What can be said back, and which of them are out of reach.
   *
   * An answer barred by its own conditions is shown greyed rather than left
   * out, when the author asked for that: knowing there is something here you
   * cannot do yet is sometimes the point of it.
   */
  options: { text: string; barred: boolean }[];
}

export function encodeDialogue(packet: DialoguePacket): Uint8Array {
  const writer = new ByteWriter(512);
  writer
    .writeU8(ServerOp.Dialogue)
    .writeString(packet.name)
    .writeVarInt(packet.entityId)
    .writeString(packet.text);

  const options = packet.options.slice(0, 12);
  writer.writeU8(options.length);
  for (const option of options) writer.writeString(option.text).writeBool(option.barred);

  return writer.toUint8Array();
}

export function decodeDialogue(reader: ByteReader): DialoguePacket {
  const name = reader.readString(48);
  const entityId = reader.readVarInt();
  const text = reader.readString(400);

  const count = reader.readU8();
  const options: DialoguePacket['options'] = [];
  for (let i = 0; i < count; i++) {
    options.push({ text: reader.readString(120), barred: reader.readBool() });
  }

  return { name, entityId, text, options };
}

/** Which answer was chosen. Anything out of range closes the conversation. */
export function encodeDialogueChoice(option: number): Uint8Array {
  return new ByteWriter(4).writeU8(ClientOp.DialogueChoice).writeU8(option).toUint8Array();
}

export function decodeDialogueChoice(reader: ByteReader): number {
  return reader.readU8();
}

/**
 * How far along every quest is, and what flags are carried.
 *
 * Sent at login and whenever something changes it, so the journal is never
 * asking and never stale.
 */
/** Which quest somebody is following, or an empty name to follow none. */
export interface TrackQuestPacket {
  quest: string;
}

export function encodeTrackQuest(packet: TrackQuestPacket): Uint8Array {
  return new ByteWriter(64).writeU8(ClientOp.TrackQuest).writeString(packet.quest).toUint8Array();
}

export function decodeTrackQuest(reader: ByteReader): TrackQuestPacket {
  return { quest: reader.readString(48) };
}

export interface ProgressPacket {
  quests: { id: string; step: number }[];
  flags: { name: string; value: string }[];
}

export function encodeProgress(packet: ProgressPacket): Uint8Array {
  const quests = packet.quests.slice(0, 128);
  const flags = packet.flags.slice(0, 128);

  const writer = new ByteWriter(64 + quests.length * 56 + flags.length * 100);
  writer.writeU8(ServerOp.Progress).writeVarInt(quests.length);
  for (const quest of quests) writer.writeString(quest.id).writeVarInt(quest.step);

  writer.writeVarInt(flags.length);
  for (const flag of flags) writer.writeString(flag.name).writeString(flag.value);

  return writer.toUint8Array();
}

export function decodeProgress(reader: ByteReader): ProgressPacket {
  const questCount = reader.readVarInt();
  const quests: { id: string; step: number }[] = [];
  for (let i = 0; i < questCount; i++) {
    quests.push({ id: reader.readString(48), step: reader.readVarInt() });
  }

  const flagCount = reader.readVarInt();
  const flags: { name: string; value: string }[] = [];
  for (let i = 0; i < flagCount; i++) {
    flags.push({ name: reader.readString(48), value: reader.readString(48) });
  }

  return { quests, flags };
}

/** Making something at a station, or in the bag. Count zero means as many as possible. */
export interface CraftPacket {
  objectId: number;
  tx: number;
  tz: number;
  plane: number;
  action: string;
  count: number;
  /**
   * Which of them was chosen, by what it makes.
   *
   * One menu entry can start more than one recipe: an anvil makes a blade out
   * of a copper bar and a different blade out of an iron one, both by pressing
   * Smith. Nought asks for whichever one there is, which is what a station with
   * a single recipe means.
   */
  outputId: number;
}

export function encodeCraft(packet: CraftPacket): Uint8Array {
  const writer = new ByteWriter(64);
  writer
    .writeU8(ClientOp.Craft)
    .writeVarInt(packet.objectId)
    .writeI32(packet.tx)
    .writeI32(packet.tz)
    .writeU8(packet.plane)
    .writeString(packet.action)
    .writeVarInt(packet.count)
    .writeVarInt(packet.outputId);
  return writer.toUint8Array();
}

export function decodeCraft(reader: ByteReader): CraftPacket {
  return {
    objectId: reader.readVarInt(),
    tx: reader.readI32(),
    tz: reader.readI32(),
    plane: reader.readU8(),
    action: reader.readString(24),
    count: reader.readVarInt(),
    outputId: reader.readVarInt(),
  };
}

/**
 * Using one thing on another.
 *
 * The target is whatever the second click landed on: another slot, a piece of
 * scenery, somebody standing there. What it comes to is the server's business.
 */
export interface UseOnPacket {
  slot: number;
  kind: number;
  /** A slot for an item, an entity id for somebody, an object id for scenery. */
  id: number;
  tx: number;
  tz: number;
  plane: number;
  /**
   * Whether to keep going until there is nothing left to do it with.
   *
   * Shift held. Feeding a fire one log at a time is how somebody trains a
   * skill, and doing it one click at a time is how they stop.
   */
  keepGoing: boolean;
}

export function encodeUseOn(packet: UseOnPacket): Uint8Array {
  const writer = new ByteWriter(48);
  writer
    .writeU8(ClientOp.UseOn)
    .writeVarInt(packet.slot)
    .writeU8(packet.kind)
    .writeVarInt(packet.id)
    .writeI32(packet.tx)
    .writeI32(packet.tz)
    .writeU8(packet.plane)
    .writeBool(packet.keepGoing);
  return writer.toUint8Array();
}

export function decodeUseOn(reader: ByteReader): UseOnPacket {
  return {
    slot: reader.readVarInt(),
    kind: reader.readU8(),
    id: reader.readVarInt(),
    tx: reader.readI32(),
    tz: reader.readI32(),
    plane: reader.readU8(),
    keepGoing: reader.readBool(),
  };
}

/**
 * A crafting station, opened or shut.
 *
 * Sent when somebody reaches one rather than when they click it, and again with
 * `open` false the moment they walk away from it: a window for a place you are
 * no longer standing at is a window that lies.
 */
export interface CraftingStatePacket {
  open: boolean;
  objectId: number;
  name: string;
  tx: number;
  tz: number;
  plane: number;
}

export function encodeCraftingState(packet: CraftingStatePacket): Uint8Array {
  const writer = new ByteWriter(64);
  writer
    .writeU8(ServerOp.CraftingState)
    .writeBool(packet.open)
    .writeVarInt(packet.objectId)
    .writeString(packet.name)
    .writeI32(packet.tx)
    .writeI32(packet.tz)
    .writeU8(packet.plane);
  return writer.toUint8Array();
}

export function decodeCraftingState(reader: ByteReader): CraftingStatePacket {
  return {
    open: reader.readBool(),
    objectId: reader.readVarInt(),
    name: reader.readString(48),
    tx: reader.readI32(),
    tz: reader.readI32(),
    plane: reader.readU8(),
  };
}

/** One region's place in the world, as the map draws it. */
export interface RegionPlaceWire {
  id: string;
  name: string;
  originX: number;
  originZ: number;
  width: number;
  height: number;
  /**
   * Which layer of the world it is on, nought being the overworld.
   *
   * Travels shifted so it is never negative, because a varint has no sign and
   * a layer under the ground is exactly what this is for.
   */
  layer: number;
}

/**
 * The arrangement of the world.
 *
 * Sent once, after the region. The world is meant to be continuous, so the map
 * behind the corner map is a map of everywhere rather than of the one region
 * somebody happens to be standing in.
 */
export interface WorldPlanPacket {
  regions: RegionPlaceWire[];
  /** Which of them the player is standing in. */
  here: string;
  /**
   * A picture of each place, keyed by its id.
   *
   * Without these the map of everywhere can only draw the ground of the one
   * region somebody is standing in, and every other place is an empty box with
   * a name on it, which is not a map. A few kilobytes each, sent once.
   */
  charts: Record<string, Chart>;
}

/**
 * A picture of one place, coarse enough to send with the arrangement.
 *
 * Loosely typed on its way out, because a chart read back out of a file is a
 * plain list of numbers and a chart off the wire is a typed one. Both are lists
 * of numbers to whoever is writing them down.
 */
export interface ChartWire {
  step: number;
  width: number;
  height: number;
  kinds: ArrayLike<number>;
  heights: ArrayLike<number>;
  /** Which named area each block is in, one past the start of `areas`. */
  area: ArrayLike<number>;
  /** What those areas are called, and where they sit in the hierarchy. */
  areas: ChartArea[];
}

/**
 * A named part of a place, as the map draws it.
 *
 * The continent is three regions across, each with a north and a south part,
 * and each part holds areas and towns. The area is the name somebody is shown;
 * the two above it are for saying it in full, as in South Kestral, The Glass
 * Waste.
 */
export interface ChartArea {
  name: string;
  about?: string;
  region?: string;
  part?: string;
  town?: boolean;
}

/** The same, as the client holds it after reading. */
export interface Chart extends Omit<ChartWire, 'kinds' | 'heights' | 'area'> {
  kinds: Uint8Array;
  heights: Uint8Array;
  area: Uint8Array;
}

/** A chart's areas, over the wire. Runs first, because most of a map is one area. */
function writeChartAreas(writer: ByteWriter, chart: ChartWire): void {
  writeRuns(writer, chart.area);
  writer.writeU8(Math.min(255, chart.areas.length));

  for (const area of chart.areas.slice(0, 255)) {
    writer
      .writeString(area.name)
      .writeString(area.about ?? '')
      .writeString(area.region ?? '')
      .writeString(area.part ?? '')
      .writeBool(area.town === true);
  }
}

function readChartAreas(reader: ByteReader, blocks: number): Pick<Chart, 'area' | 'areas'> {
  const area = readRuns(reader, blocks);
  const count = reader.readU8();
  const areas = new Array<ChartArea>(count);

  for (let at = 0; at < count; at++) {
    areas[at] = {
      name: reader.readString(64),
      about: reader.readString(192),
      region: reader.readString(64),
      part: reader.readString(32),
      town: reader.readBool(),
    };
  }

  return { area, areas };
}

export function encodeWorldPlan(
  packet: Omit<WorldPlanPacket, 'charts'> & { charts: Record<string, ChartWire> },
): Uint8Array {
  const writer = new ByteWriter(64 + packet.regions.length * 80);
  writer.writeU8(ServerOp.WorldPlan).writeString(packet.here);

  const regions = packet.regions.slice(0, 255);
  writer.writeU8(regions.length);
  for (const region of regions) {
    writer
      .writeString(region.id)
      .writeString(region.name)
      .writeVarInt(region.originX)
      .writeVarInt(region.originZ)
      .writeVarInt(region.width)
      .writeVarInt(region.height)
      .writeVarInt(Math.max(0, Math.min(8, region.layer + 4)));
  }

  // A picture of each of them, run length encoded, which an ocean and a
  // mountain range both compress well under.
  const charts = regions.filter((region) => packet.charts[region.id] !== undefined);
  writer.writeU8(charts.length);

  for (const region of charts) {
    const chart = packet.charts[region.id];
    if (!chart) continue;

    writer.writeString(region.id).writeU8(chart.step).writeU16(chart.width).writeU16(chart.height);
    writeRuns(writer, chart.kinds);
    writeRuns(writer, chart.heights);
    writeChartAreas(writer, chart);
  }

  return writer.toUint8Array();
}

export function decodeWorldPlan(reader: ByteReader): WorldPlanPacket {
  const here = reader.readString(64);
  const count = reader.readU8();

  const regions = new Array<RegionPlaceWire>(count);
  for (let i = 0; i < count; i++) {
    regions[i] = {
      id: reader.readString(64),
      name: reader.readString(64),
      originX: reader.readVarInt(),
      originZ: reader.readVarInt(),
      width: reader.readVarInt(),
      height: reader.readVarInt(),
      layer: reader.readVarInt() - 4,
    };
  }

  const charts: Record<string, Chart> = {};
  const charted = reader.readU8();

  for (let i = 0; i < charted; i++) {
    const id = reader.readString(64);
    const step = reader.readU8();
    const width = reader.readU16();
    const height = reader.readU16();

    charts[id] = {
      step,
      width,
      height,
      kinds: readRuns(reader, width * height),
      heights: readRuns(reader, width * height),
      ...readChartAreas(reader, width * height),
    };
  }

  return { regions, here, charts };
}

/**
 * Something crossing the ground between two fighters.
 *
 * The blow has already happened by the time this arrives: the damage is sent on
 * its own and this is what makes it read as having come from over there. Sent
 * as two entities rather than two tiles, so it follows whoever moves while it is
 * in the air.
 */
export interface FlyingPacket {
  fromId: number;
  toId: number;
  /**
   * What it is, by name: arrow, bolt, air, water, earth, fire.
   *
   * Free text, because what can be thrown is content rather than a list the
   * protocol should be keeping. The client draws what it knows and a plain
   * streak for anything else.
   */
  kind: string;
  /** How long it takes to cross, in tenths of a second. */
  tenths: number;
  /**
   * The spell it is, when it is one: the client draws it the way that spell
   * says, from the book it already has. Nought for anything else.
   */
  spell: number;
  /** Where it lands when it is aimed at the ground rather than at somebody. */
  tx: number;
  tz: number;
}

export function encodeFlying(packet: FlyingPacket): Uint8Array {
  return new ByteWriter(48)
    .writeU8(ServerOp.Flying)
    .writeU32(packet.fromId)
    .writeU32(packet.toId)
    .writeString(packet.kind)
    .writeU8(Math.max(1, Math.min(255, Math.round(packet.tenths))))
    .writeVarInt(packet.spell)
    .writeVarInt(Math.max(0, packet.tx))
    .writeVarInt(Math.max(0, packet.tz))
    .toUint8Array();
}

export function decodeFlying(reader: ByteReader): FlyingPacket {
  return {
    fromId: reader.readU32(),
    toId: reader.readU32(),
    kind: reader.readString(24),
    tenths: reader.readU8(),
    spell: reader.readVarInt(),
    tx: reader.readVarInt(),
    tz: reader.readVarInt(),
  };
}

/**
 * What time it is in the world.
 *
 * The length of a day and the moment the server read its own clock, so the
 * client can run the day itself rather than being told about every minute of
 * it. `forced` is a stretch of day somebody is holding it at while they work,
 * and empty means it is running on its own.
 */
export interface TimeOfDayPacket {
  dayMs: number;
  now: number;
  forced: string;
}

export function encodeTimeOfDay(packet: TimeOfDayPacket): Uint8Array {
  return (
    new ByteWriter(48)
      .writeU8(ServerOp.TimeOfDay)
      .writeVarInt(Math.max(1000, Math.round(packet.dayMs)))
      // Milliseconds do not fit a varint comfortably, so the moment travels as
      // two halves of a second and a remainder, which every clock has.
      .writeVarInt(Math.floor(packet.now / 1000))
      .writeU16(packet.now % 1000)
      .writeString(packet.forced)
      .toUint8Array()
  );
}

export function decodeTimeOfDay(reader: ByteReader): TimeOfDayPacket {
  const dayMs = reader.readVarInt();
  const seconds = reader.readVarInt();
  const milliseconds = reader.readU16();

  return { dayMs, now: seconds * 1000 + milliseconds, forced: reader.readString(16) };
}

// ---------------------------------------------------------------------------
// Ground, a square at a time
// ---------------------------------------------------------------------------

/**
 * One square of ground and everything standing on it.
 *
 * The same squares the world is stored in and drawn in, so a square that
 * arrives is a square that is written straight into the arrays the client
 * already holds and a mesh that is already the right shape.
 *
 * Heights are corners, one more than the tiles in each direction, and the
 * squares either side of a join both carry the corners along it. They agree,
 * because they were the same number when the ground was cut up.
 */
export interface GroundPacket {
  /** Which region this belongs to, so a square in flight when somebody travels is dropped. */
  region: string;
  /** Where the square starts, in the region's own tiles. */
  tx: number;
  tz: number;
  /** How much of it is real, which is short on the last row of a region. */
  width: number;
  height: number;
  heights: Float32Array;
  flags: Uint8Array;
  /** Collision on the floors above, one array per plane above the ground. */
  upperFlags: Uint8Array[];
  ground: Uint8Array;
  water: Float32Array;
  /** Which named area each tile is in, one past the start of the chart's areas. */
  area: Uint8Array;
  /**
   * Which way the water runs, on the tiles of this square that have a current.
   *
   * Sparse, because almost no tile has one: the sea lies still and so does every
   * lake. `at` is a tile index within the square, and the pair is tiles a second
   * along each axis.
   */
  flow: { at: number; x: number; z: number }[];
  objects: PlacedObject[];
}

/** What the server hands over, which is whatever it read out of a file. */
export interface GroundSource extends Omit<
  GroundPacket,
  'heights' | 'flags' | 'upperFlags' | 'ground' | 'water' | 'area' | 'flow'
> {
  heights: ArrayLike<number>;
  flags: ArrayLike<number>;
  upperFlags: ArrayLike<number>[];
  ground: ArrayLike<number>;
  water: ArrayLike<number>;
  area: ArrayLike<number>;
  /** Left out by a square with no current anywhere in it, which is most of them. */
  flow?: readonly { at: number; x: number; z: number }[];
}

/**
 * Water is nought over most of the world and one level over the rest of it, so
 * the same trick pays here as well, at two bytes a value instead of one.
 */
function writeLevels(writer: ByteWriter, held: ArrayLike<number>): void {
  const first = Math.round((held[0] ?? 0) * 32);
  let same = true;
  for (let at = 1; at < held.length && same; at++)
    same = Math.round((held[at] ?? 0) * 32) === first;

  if (same) {
    writer.writeU8(PLAIN_UNIFORM).writeI16(first);
    return;
  }

  writer.writeU8(PLAIN_EXPLICIT);
  for (let at = 0; at < held.length; at++) writer.writeI16(Math.round((held[at] ?? 0) * 32));
}

function readLevels(reader: ByteReader, count: number): Float32Array {
  const held = new Float32Array(count);

  if (reader.readU8() === PLAIN_UNIFORM) return held.fill(reader.readI16() / 32);
  for (let at = 0; at < count; at++) held[at] = reader.readI16() / 32;
  return held;
}

export function encodeGround(square: GroundSource): Uint8Array {
  const writer = new ByteWriter(square.heights.length * 2 + square.flags.length * 3 + 1024);

  writer
    .writeU8(ServerOp.GroundData)
    .writeString(square.region)
    .writeU16(square.tx)
    .writeU16(square.tz)
    .writeU16(square.width)
    .writeU16(square.height);

  writeLevels(writer, square.heights);
  writeBytes(writer, square.flags);

  writer.writeU8(square.upperFlags.length);
  for (const plane of square.upperFlags) writeBytes(writer, plane);

  writeBytes(writer, square.ground);
  writeLevels(writer, square.water);

  // Which area each tile belongs to, so the map in the corner can say where
  // somebody is standing to the tile rather than to the nearest chart block.
  writeBytes(writer, square.area);

  // The current, a hundredth of a tile a second to the step, which is finer
  // than anybody can see the surface move.
  const flow = square.flow ?? [];
  writer.writeU16(flow.length);
  for (const one of flow) {
    writer.writeU16(one.at);
    writer.writeI8(Math.max(-127, Math.min(127, Math.round(one.x * 100))));
    writer.writeI8(Math.max(-127, Math.min(127, Math.round(one.z * 100))));
  }

  writeObjects(writer, square.objects);
  return writer.toUint8Array();
}

export function decodeGround(reader: ByteReader): GroundPacket {
  const region = reader.readString(64);
  const tx = reader.readU16();
  const tz = reader.readU16();
  const width = reader.readU16();
  const height = reader.readU16();

  const heights = readLevels(reader, (width + 1) * (height + 1));
  const flags = readBytes(reader, width * height);

  const upperCount = reader.readU8();
  const upperFlags: Uint8Array[] = [];
  for (let plane = 0; plane < upperCount; plane++) {
    upperFlags.push(readBytes(reader, width * height));
  }

  const ground = readBytes(reader, width * height);
  const water = readLevels(reader, width * height);
  const area = readBytes(reader, width * height);

  const running = reader.readU16();
  const flow: { at: number; x: number; z: number }[] = [];
  for (let one = 0; one < running; one++) {
    flow.push({ at: reader.readU16(), x: reader.readI8() / 100, z: reader.readI8() / 100 });
  }

  return {
    region,
    tx,
    tz,
    width,
    height,
    heights,
    flags,
    upperFlags,
    ground,
    water,
    area,
    flow,
    objects: readObjects(reader),
  };
}

/** Squares somebody has walked far enough away from to forget. */
export interface GroundGonePacket {
  region: string;
  squares: { tx: number; tz: number }[];
}

/**
 * A moment of black, so something can happen out of sight.
 *
 * No body at all: the whole of it is that it happened. How long the dip lasts is
 * the client's business, because it is a matter of how it looks rather than of
 * what is true.
 */
export function encodeBlink(): Uint8Array {
  return new ByteWriter(1).writeU8(ServerOp.Blink).toUint8Array();
}

export function encodeGroundGone(gone: GroundGonePacket): Uint8Array {
  const writer = new ByteWriter(gone.squares.length * 4 + 96);
  writer.writeU8(ServerOp.GroundGone).writeString(gone.region).writeVarInt(gone.squares.length);

  for (const square of gone.squares) writer.writeU16(square.tx).writeU16(square.tz);
  return writer.toUint8Array();
}

export function decodeGroundGone(reader: ByteReader): GroundGonePacket {
  const region = reader.readString(64);
  const count = reader.readVarInt();
  const squares = new Array<{ tx: number; tz: number }>(count);

  for (let at = 0; at < count; at++) squares[at] = { tx: reader.readU16(), tz: reader.readU16() };
  return { region, squares };
}

// ---------------------------------------------------------------------------
// Being asked for something
// ---------------------------------------------------------------------------

/**
 * What somebody is asking for.
 *
 * Two kinds share one packet because they arrive the same way and are answered
 * the same way: a name, a thing being asked, and a yes or a no. Splitting them
 * would mean two packets that must be kept in step for no gain.
 */
export const AskKind = {
  Friend: 0,
  Trade: 1,
  Party: 2,
} as const;

export type AskKindValue = (typeof AskKind)[keyof typeof AskKind];

export interface AskedPacket {
  kind: AskKindValue;
  name: string;
}

export function encodeAsked(packet: AskedPacket): Uint8Array {
  return new ByteWriter(64)
    .writeU8(ServerOp.Asked)
    .writeU8(packet.kind)
    .writeString(packet.name)
    .toUint8Array();
}

export function decodeAsked(reader: ByteReader): AskedPacket {
  return { kind: reader.readU8() as AskKindValue, name: reader.readString(32) };
}

/**
 * Which kinds of request this player will hear.
 *
 * Sent when somebody changes the setting and again when they log in, because the
 * setting lives on their machine and the server is the thing that has to act on
 * it. Turning one off is not a filter on the way in: the request is refused, and
 * whoever sent it is told so rather than being left waiting.
 */
export interface AllowRequestsPacket {
  friends: boolean;
  trades: boolean;
}

export function encodeAllowRequests(packet: AllowRequestsPacket): Uint8Array {
  return new ByteWriter(8)
    .writeU8(ClientOp.AllowRequests)
    .writeBool(packet.friends)
    .writeBool(packet.trades)
    .toUint8Array();
}

export function decodeAllowRequests(reader: ByteReader): AllowRequestsPacket {
  return { friends: reader.readBool(), trades: reader.readBool() };
}

// ---------------------------------------------------------------------------
// Trading
// ---------------------------------------------------------------------------

/**
 * What somebody is doing about a trade.
 *
 * Asking and answering are here as well as offering, because a trade is one
 * conversation from the first request to the last agreement and splitting it
 * across packets would mean keeping several of them in step.
 */
export const TradeAct = {
  /** Asks somebody to trade. */
  Ask: 0,
  /** Answers a request that has arrived. */
  Accept: 1,
  Decline: 2,
  /** Puts something up, or takes it back. Count is how many of a stack. */
  Offer: 3,
  Withdraw: 4,
  /**
   * Agrees to the half of the trade that is showing.
   *
   * On the first screen this moves both to the second when they have both
   * agreed; on the second it hands the goods over. Pressed again after the other
   * one has changed something, it agrees to the new offer rather than the old.
   */
  Agree: 5,
  /** Walks away from it. */
  Close: 6,
} as const;

export type TradeActValue = (typeof TradeAct)[keyof typeof TradeAct];

export interface TradeRequest {
  act: TradeActValue;
  /** Who, for an ask or an answer. Empty for everything else. */
  name: string;
  /** Which slot, for putting something up or taking it back. */
  slot: number;
  /** How many of a stack. Nought means all of it. */
  count: number;
}

export function encodeTradeRequest(packet: TradeRequest): Uint8Array {
  return new ByteWriter(64)
    .writeU8(ClientOp.Trade)
    .writeU8(packet.act)
    .writeString(packet.name)
    .writeVarInt(packet.slot)
    .writeVarInt(packet.count)
    .toUint8Array();
}

export function decodeTradeRequest(reader: ByteReader): TradeRequest {
  return {
    act: reader.readU8() as TradeActValue,
    name: reader.readString(32),
    slot: reader.readVarInt(),
    count: reader.readVarInt(),
  };
}

/**
 * How a trade stands.
 *
 * Sent whenever anything about it changes, and once with `open` false when it
 * ends, however it ended. The reason is carried so the client can say what
 * happened rather than having a window disappear.
 */
export interface TradeStatePacket {
  open: boolean;
  /** Who it is with. */
  name: string;
  /** One while the goods are being chosen, two while they are being confirmed. */
  stage: number;
  /** Whether each side has agreed to what is showing. */
  agreed: boolean;
  theyAgreed: boolean;
  /** Why it ended, said plainly. Empty while it is running. */
  ended: string;
}

export function encodeTradeState(packet: TradeStatePacket): Uint8Array {
  return new ByteWriter(128)
    .writeU8(ServerOp.TradeState)
    .writeBool(packet.open)
    .writeString(packet.name)
    .writeU8(packet.stage)
    .writeBool(packet.agreed)
    .writeBool(packet.theyAgreed)
    .writeString(packet.ended)
    .toUint8Array();
}

export function decodeTradeState(reader: ByteReader): TradeStatePacket {
  return {
    open: reader.readBool(),
    name: reader.readString(32),
    stage: reader.readU8(),
    agreed: reader.readBool(),
    theyAgreed: reader.readBool(),
    ended: reader.readString(120),
  };
}

/* ---------------------------------------------------------------------------
   Spells and prayers
   --------------------------------------------------------------------------- */

/** What a spell is cast at, on the wire. */
export const CastTarget = { Self: 0, Entity: 1, Tile: 2, Item: 3 } as const;
export type CastTargetValue = (typeof CastTarget)[keyof typeof CastTarget];

export interface CastSpellPacket {
  spell: number;
  target: CastTargetValue;
  /** The entity cast at, or the bag slot for an item. */
  id: number;
  tx: number;
  tz: number;
  plane: number;
}

export function encodeCastSpell(packet: CastSpellPacket): Uint8Array {
  return new ByteWriter(24)
    .writeU8(ClientOp.CastSpell)
    .writeVarInt(packet.spell)
    .writeU8(packet.target)
    .writeVarInt(packet.id)
    .writeVarInt(Math.max(0, packet.tx))
    .writeVarInt(Math.max(0, packet.tz))
    .writeU8(packet.plane)
    .toUint8Array();
}

export function decodeCastSpell(reader: ByteReader): CastSpellPacket {
  return {
    spell: reader.readVarInt(),
    target: Math.min(3, reader.readU8()) as CastTargetValue,
    id: reader.readVarInt(),
    tx: reader.readVarInt(),
    tz: reader.readVarInt(),
    plane: Math.min(3, reader.readU8()),
  };
}

export function encodeSetAutocast(spell: number): Uint8Array {
  return new ByteWriter(8).writeU8(ClientOp.SetAutocast).writeVarInt(spell).toUint8Array();
}

export function decodeSetAutocast(reader: ByteReader): number {
  return reader.readVarInt();
}

export function encodeTogglePrayer(prayer: number, on: boolean): Uint8Array {
  return new ByteWriter(8)
    .writeU8(ClientOp.TogglePrayer)
    .writeVarInt(prayer)
    .writeBool(on)
    .toUint8Array();
}

export function decodeTogglePrayer(reader: ByteReader): { prayer: number; on: boolean } {
  return { prayer: reader.readVarInt(), on: reader.readBool() };
}

/** The spells and prayers, as the content JSON the tools write. */
export function encodeSpellbook(json: string): Uint8Array {
  return new ByteWriter(json.length + 16)
    .writeU8(ServerOp.Spellbook)
    .writeString(json)
    .toUint8Array();
}

export function decodeSpellbook(reader: ByteReader): string {
  return reader.readString(2_000_000);
}

export interface DevotionPacket {
  /** Faith left and the most there can be, in tenths of a point. */
  faith: number;
  maxFaith: number;
  prayers: number[];
  autocast: number;
  /** What is lasting on them, and how many ticks each has left. */
  statuses: { name: string; ticks: number; helpful: boolean }[];
  /**
   * Whether something lasting on them holds them where they stand, so their
   * client stops predicting a walk the server will not take.
   */
  bound: boolean;
}

export function encodeDevotion(packet: DevotionPacket): Uint8Array {
  const writer = new ByteWriter(64)
    .writeU8(ServerOp.Devotion)
    .writeVarInt(Math.max(0, Math.round(packet.faith)))
    .writeVarInt(Math.max(0, Math.round(packet.maxFaith)))
    .writeVarInt(packet.prayers.length);
  for (const id of packet.prayers) writer.writeVarInt(id);
  writer.writeVarInt(packet.autocast).writeVarInt(packet.statuses.length);
  for (const status of packet.statuses) {
    writer
      .writeString(status.name)
      .writeVarInt(Math.max(0, status.ticks))
      .writeBool(status.helpful);
  }
  writer.writeBool(packet.bound);
  return writer.toUint8Array();
}

export function decodeDevotion(reader: ByteReader): DevotionPacket {
  const faith = reader.readVarInt();
  const maxFaith = reader.readVarInt();
  const count = Math.min(256, reader.readVarInt());
  const prayers: number[] = [];
  for (let i = 0; i < count; i++) prayers.push(reader.readVarInt());
  const autocast = reader.readVarInt();
  const many = Math.min(64, reader.readVarInt());
  const statuses: DevotionPacket['statuses'] = [];
  for (let i = 0; i < many; i++) {
    statuses.push({
      name: reader.readString(32),
      ticks: reader.readVarInt(),
      helpful: reader.readBool(),
    });
  }
  return { faith, maxFaith, prayers, autocast, statuses, bound: reader.readBool() };
}

export function encodeSoundLibrary(json: string): Uint8Array {
  return new ByteWriter(json.length + 16)
    .writeU8(ServerOp.SoundLibrary)
    .writeString(json)
    .toUint8Array();
}

export function decodeSoundLibrary(reader: ByteReader): string {
  return reader.readString(1_000_000);
}

/** Everything somebody does at the exchange. */
export const ExchangeAct = {
  /** Puts one up: buying or selling, so many, at so much each. */
  Offer: 0,
  /** Stops one that is still standing. */
  Cancel: 1,
  /** Takes what an offer is owed into the bag. */
  Collect: 2,
  /** Shuts the window, which the world remembers so it can stop sending. */
  Close: 3,
  /** Opens it. Offers are left standing anywhere, so this asks from anywhere. */
  Open: 4,
} as const;

export type ExchangeActValue = (typeof ExchangeAct)[keyof typeof ExchangeAct];

export interface ExchangePacket {
  act: ExchangeActValue;
  slot: number;
  itemId: number;
  selling: boolean;
  price: number;
  quantity: number;
}

export function encodeExchange(packet: ExchangePacket): Uint8Array {
  return new ByteWriter(32)
    .writeU8(ClientOp.Exchange)
    .writeU8(packet.act)
    .writeU8(packet.slot)
    .writeVarInt(Math.max(0, packet.itemId))
    .writeBool(packet.selling)
    .writeVarInt(Math.max(0, Math.round(packet.price)))
    .writeVarInt(Math.max(0, Math.round(packet.quantity)))
    .toUint8Array();
}

export function decodeExchange(reader: ByteReader): ExchangePacket {
  return {
    act: Math.min(4, reader.readU8()) as ExchangeActValue,
    slot: reader.readU8(),
    itemId: reader.readVarInt(),
    selling: reader.readBool(),
    price: reader.readVarInt(),
    quantity: reader.readVarInt(),
  };
}

/** One offer as the window draws it. */
export interface ExchangeOfferWire {
  slot: number;
  itemId: number;
  selling: boolean;
  price: number;
  quantity: number;
  filled: number;
  owedItems: number;
  owedCoins: number;
  open: boolean;
}

export interface ExchangeWire {
  open: boolean;
  offers: ExchangeOfferWire[];
  /** What things have been going for lately, by item id, for whatever is on screen. */
  rates: Record<string, number>;
}

export function encodeExchangeState(json: string): Uint8Array {
  return new ByteWriter(json.length + 16)
    .writeU8(ServerOp.ExchangeState)
    .writeString(json)
    .toUint8Array();
}

export function decodeExchangeState(reader: ByteReader): string {
  return reader.readString(100_000);
}

/** Everything somebody does about a guild. */
export const GuildAct = {
  /** Starts one, named. */
  Found: 0,
  /** Asks somebody in, and the two answers to that. */
  Ask: 1,
  Accept: 2,
  Decline: 3,
  Leave: 4,
  /** Puts somebody out, and changes what somebody may do. */
  Remove: 5,
  MakeOfficer: 6,
  MakeMember: 7,
  HandOver: 8,
  /** Writes the notice everybody sees when they log in. */
  Notice: 9,
} as const;

export type GuildActValue = (typeof GuildAct)[keyof typeof GuildAct];

export interface GuildPacket {
  act: GuildActValue;
  /** A guild's name, a person's name, or the notice, depending on the act. */
  text: string;
}

export function encodeGuild(packet: GuildPacket): Uint8Array {
  return new ByteWriter(256)
    .writeU8(ClientOp.Guild)
    .writeU8(packet.act)
    .writeString(packet.text)
    .toUint8Array();
}

export function decodeGuild(reader: ByteReader): GuildPacket {
  return { act: Math.min(9, reader.readU8()) as GuildActValue, text: reader.readString(200) };
}

/** One person in a guild, as the panel draws them. */
export interface GuildMemberWire {
  name: string;
  rank: 'owner' | 'officer' | 'member';
  online: boolean;
}

export interface GuildWire {
  /** Empty for somebody who is in no guild. */
  name: string;
  motd: string;
  rank: '' | 'owner' | 'officer' | 'member';
  members: GuildMemberWire[];
  /** A guild waiting on an answer from this person. */
  asked: { name: string; by: string } | null;
}

export function encodeGuildState(json: string): Uint8Array {
  return new ByteWriter(json.length + 16)
    .writeU8(ServerOp.GuildState)
    .writeString(json)
    .toUint8Array();
}

export function decodeGuildState(reader: ByteReader): string {
  return reader.readString(200_000);
}

/** Everything somebody does about a party. */
export const PartyAct = {
  /** Asks somebody to join. */
  Ask: 0,
  Accept: 1,
  Decline: 2,
  /** Walks out of the one they are in. */
  Leave: 3,
  /** Points the party at somewhere, or takes the point away. */
  Point: 4,
  Unpoint: 5,
} as const;

export type PartyActValue = (typeof PartyAct)[keyof typeof PartyAct];

export interface PartyPacket {
  act: PartyActValue;
  /** Who it is about, for asking and answering. Empty otherwise. */
  name: string;
  tx: number;
  tz: number;
  layer: number;
}

export function encodeParty(packet: PartyPacket): Uint8Array {
  return new ByteWriter(64)
    .writeU8(ClientOp.Party)
    .writeU8(packet.act)
    .writeString(packet.name)
    .writeVarInt(Math.max(0, Math.round(packet.tx)))
    .writeVarInt(Math.max(0, Math.round(packet.tz)))
    .writeU8(Math.max(0, Math.min(3, Math.round(packet.layer))))
    .toUint8Array();
}

export function decodeParty(reader: ByteReader): PartyPacket {
  return {
    act: Math.min(5, reader.readU8()) as PartyActValue,
    name: reader.readString(32),
    tx: reader.readVarInt(),
    tz: reader.readVarInt(),
    layer: reader.readU8(),
  };
}

/** One member of a party, as the panel and the maps draw them. */
export interface PartyMemberWire {
  name: string;
  tx: number;
  tz: number;
  layer: number;
  health: number;
  maxHealth: number;
  /** Whether they are in the same part of the world, so their dot means anything. */
  near: boolean;
}

export interface PartyWire {
  leader: string;
  members: PartyMemberWire[];
  /** Where the party is headed, and who said so. */
  point: { tx: number; tz: number; layer: number; by: string } | null;
}

export function encodePartyState(json: string): Uint8Array {
  return new ByteWriter(json.length + 16)
    .writeU8(ServerOp.PartyState)
    .writeString(json)
    .toUint8Array();
}

export function decodePartyState(reader: ByteReader): string {
  return reader.readString(100_000);
}

export function encodeCollection(json: string): Uint8Array {
  return new ByteWriter(json.length + 16)
    .writeU8(ServerOp.Collection)
    .writeString(json)
    .toUint8Array();
}

export function decodeCollection(reader: ByteReader): string {
  return reader.readString(1_000_000);
}

export interface PlaySoundPacket {
  /** A cue from the game's list, or a sound from the library by its name. */
  name: string;
  cue: boolean;
  /** Who it comes from, followed as they move, or nought for a place. */
  entityId: number;
  tx: number;
  tz: number;
}

export function encodePlaySound(packet: PlaySoundPacket): Uint8Array {
  return new ByteWriter(16 + packet.name.length * 3)
    .writeU8(ServerOp.PlaySound)
    .writeString(packet.name)
    .writeBool(packet.cue)
    .writeVarInt(packet.entityId)
    .writeVarInt(Math.max(0, packet.tx))
    .writeVarInt(Math.max(0, packet.tz))
    .toUint8Array();
}

export function decodePlaySound(reader: ByteReader): PlaySoundPacket {
  return {
    name: reader.readString(48),
    cue: reader.readBool(),
    entityId: reader.readVarInt(),
    tx: reader.readVarInt(),
    tz: reader.readVarInt(),
  };
}

/** Somebody reported, why, and anything the reporter wanted to add. */
export interface ReportPacket {
  name: string;
  reason: string;
  note: string;
}

export function encodeReport(packet: ReportPacket): Uint8Array {
  return new ByteWriter(64 + packet.note.length * 3)
    .writeU8(ClientOp.ReportPlayer)
    .writeString(packet.name.slice(0, 32))
    .writeString(packet.reason.slice(0, 40))
    .writeString(packet.note.slice(0, 300))
    .toUint8Array();
}

export function decodeReport(reader: ByteReader): ReportPacket {
  return {
    name: reader.readString(32),
    reason: reader.readString(40),
    note: reader.readString(1200),
  };
}
